import useFixedTtsSamplePreview from '../hooks/useFixedTtsSamplePreview';
import useServerTtsVoices from '../hooks/useServerTtsVoices';
import EventSection from './Messages/Sections/EventSection';
import TitleContent from './MessagePreview/TitleContent';
import AudioCreateContent from './MessagePreview/AudioCreateContent';
import {moreAlertsLabel} from '../i18n/countLabels';
import {useTranslation} from 'react-i18next';
import { t as lnT } from '../i18n';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { QUERY, MUTATION } from '../gql-operations';
import { GET_FEATURE_FLAG } from '../gql-operations/featureFlags';

import TextareaAutosize from 'react-textarea-autosize';

import useDeleteMessagesMutation from '../hooks/useDeleteMessagesMutation';
import useTrashMessagesMutation from '../hooks/useTrashMessagesMutation';
import useUpdateMessageMutation from '../hooks/useUpdateMessageMutation';
import useRestoreMessagesMutation from '../hooks/useRestoreMessagesMutation';

import { useQuery, useMutation } from '@apollo/client';

import { format } from 'date-fns';

import Hotkeys from 'react-hot-keys';

// import FileOptions from './MessagePreview/FileOptions';

import PluginManager from './MessagePreview/Plugins/PluginManager';
import MessageReferencePreview from './MessageReferencePreview';
import { PreviewHeader as HeaderContainer, PreviewScroll, PreviewBody as MessageContainer, PreviewInfoPlacement as MessageInfoPlacement, PreviewTitleText as TitleTextDisplay } from './MessagePreview/previewLayout';
import MessageInfoSection from './MessagePreview/MessageInfoSection';
import { getFolderAccess } from '../util/folderAccess';
import NoteSection from './MessagePreview/NoteSection';
import { NOTE_EDIT_MODES } from './MessagePreview/NoteEditor';

import { AuthContext } from '../util/context';
import useCheckBrowser from '../util/useCheckBrowser';
import useUpdateCheckboxes from '../hooks/useUpdateCheckboxes';
import useUpdateStarred from '../hooks/useUpdateStarred';
import { openSettingsModal, SETTINGS_MODAL_TAB } from '../util/settingsModalEvents';
import { getReminderDueTimezone, REMINDER_WEB_INSTALLATION_ID } from '../util/reminderClientContext';
import {
    FEATURE_UPGRADE_MODAL_PRESET,
    openFeatureUpgradeModal
} from '../util/featureUpgradeModalEvents';

import { IoMdClose, IoMdOpen, IoMdPlay } from 'react-icons/io';
import { LuMessageSquareShare } from 'react-icons/lu';
import {
    FiMoreVertical,
    FiStar,
    FiCheckSquare,
    FiCheckCircle,
    FiTrash2,
    FiRotateCcw,
    FiXCircle,
    FiFolder,
    FiEdit2,
    FiCopy,
    FiDownload,
    FiBell,
    FiSearch,
    FiArrowRight,
    FiPaperclip,
    FiChevronDown
} from 'react-icons/fi';
import {
    NOTE_BACKGROUND,
    PALE_WHITE_6,
    RED,
    GREEN,
    PALE_WHITE_1,
    WHITE,
    PRIMARY_COLOR_8
} from '../appearance/Colors';
import ReactTooltip from 'react-tooltip';

import MyDropzone from '../components/MyDropzone';

import DeleteModal from '../components/Modals/DeleteModal';
import EntityMentionsSection from './MessagePreview/EntityMentionsSection';
import ExtractedArtifactsSection from './Messages/Sections/ExtractedArtifactsSection';
import CreditsRequiredModal from '../components/Modals/CreditsRequiredModal';
import Modal from './Modals/Modal';
import MessageVersionHistoryModal from './Modals/MessageVersionHistoryModal';
import ReminderPickerModal from './Modals/ReminderPickerModal';
import SearchFoldersModal, { searchActions } from './Modals/SearchFoldersModal';

import Checkbox from '../components/Folder/Components/Checkbox';
import useNotificationSystem from '../components/Notifications/useNotificationSystem';
import customAnalytics from '../util/custom-analytics';
import { cloudinaryCore } from '../util/cloudinary';
import { normalizeMarkdownForStorage, setMarkdownTaskCheckboxState } from '../util/markdownTheme';
import { evaluateFeatureFlagForUser } from '../util/featureFlagAccess';
import { downloadBlobInBrowser, isTauriEnvironment, openExternalUrl, saveBlobWithTauri } from '../util/downloadHelpers';
import { resolveDownloadFileName } from '../util/downloadFileName';
import { getSearchUrl, getStoredSearchEngine } from '../constants/searchEngines';

const MessageShareModal = React.lazy(() => import('./Modals/MessageShareModal'));

const TTS_VOICE_STORAGE_KEY = 'luckynote_tts_voice';
// TODO(tts): Consider adding additional OpenAI voices later: ash, ballad, coral, sage, verse, marin, cedar.
const TTS_DEFAULT_STYLE = 'NATURAL';


const getStoredTtsVoice = () => {
    if (typeof window === 'undefined') return '';
    const saved = window.localStorage.getItem(TTS_VOICE_STORAGE_KEY);
    return saved?.startsWith('server:') ? saved : '';
};

const voiceModalStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        color: WHITE,
        maxWidth: 480,
        width: '92vw',
        borderRadius: 18,
        border: 'none',
        background: '#222729',
        padding: 0,
        overflow: 'hidden'
    },
    overlay: {
        // Open above the workspace (2300) and standalone article reader (2400).
        zIndex: 2500
    }
};

const MessagePreviewComponent = React.forwardRef(({
    message,
    onClose,
    onOpenInApp,
    onShowInFolder,
    onMessageTrashed,
    onSelectionChange,
    onClearSelection,
    showCloseButton = true,
    dropzoneTitle = 'Upload to message',
    dropTargets = [],
    primaryDropTarget = null,
    onDropzoneFilesSelected,
    onOpenVideoWorkspace,
    hideUrlTranscriptAction = false,
    disableMoveMessage = false,
    readOnly = false,
    suppliedContentOnly = false,
    actionMenuZIndex = 1500
}, forwardedRef) => {
    const {t: lnT} = useTranslation();

    const {
        state: { showDeletedMessages, selectedFolder: accessFolder },
        context: { setSelectedMessage }
    } = React.useContext(AuthContext);
    const folderAccess = getFolderAccess(accessFolder);
    readOnly = suppliedContentOnly || readOnly || !folderAccess.canEdit;
    const canGenerate = !readOnly && folderAccess.canGenerate;
    disableMoveMessage = disableMoveMessage || !getFolderAccess(accessFolder).canManage;
    const emitMessageUpdate = React.useCallback(updatedMessage => {
        if (typeof window === 'undefined' || !updatedMessage || !updatedMessage.id) {
            return;
        }

        window.dispatchEvent(
            new CustomEvent('messageUpdated', {
                detail: {
                    message: updatedMessage
                }
            })
        );
    }, [lnT]);

    const selectPreviewMessage = React.useCallback(
        nextMessage => {
            if (typeof onSelectionChange === 'function') {
                onSelectionChange(nextMessage);
                return;
            }
            setSelectedMessage(nextMessage);
        },
        [lnT, onSelectionChange, setSelectedMessage]
    );

    const clearPreviewSelection = React.useCallback(() => {
        if (typeof onClearSelection === 'function') {
            onClearSelection();
            return;
        }
        setSelectedMessage(undefined);
    }, [lnT, onClearSelection, setSelectedMessage]);

    const applyPreviewSelectionState = React.useCallback(
        nextMessage => {
            if (!nextMessage) {
                return nextMessage;
            }

            if (nextMessage.previewMode) {
                return nextMessage;
            }

            return message?.previewMode
                ? {
                    ...nextMessage,
                    previewMode: message.previewMode
                }
                : nextMessage;
        },
        [lnT, message]
    );

    const applyLocalMessageUpdate = React.useCallback(
        updatedMessage => {
            if (!updatedMessage) {
                return;
            }
            // Only update selectedMessage if we're not closing
            // This prevents the preview from reopening after close
            if (!isClosingRef.current) {
                selectPreviewMessage(applyPreviewSelectionState(updatedMessage));
            }
            // Always emit the update event so the list updates
            emitMessageUpdate(updatedMessage);
        },
        [lnT, applyPreviewSelectionState, emitMessageUpdate, selectPreviewMessage]
    );

    const { showDefaultNotification, showCopyToClipboardNotification } = useNotificationSystem();
    const {
        state: { uploadProgress },
        context: { setUploadProgress }
    } = React.useContext(AuthContext);
    const messageContainerRef = React.useRef();
    const attachmentInputRef = React.useRef();
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showReminderTrashWarning, setShowReminderTrashWarning] = useState(false);
    const [reminderPickerMessage, setReminderPickerMessage] = useState(null);
    const [showMoveFoldersModal, setShowMoveFoldersModal] = useState(false);
    const [moveMessageTarget, setMoveMessageTarget] = useState(undefined);
    const [localFolderOverride, setLocalFolderOverride] = useState(null);
    const [voiceModalSourceMessage, setVoiceModalSourceMessage] = useState(undefined);
    const [voiceModalSource, setVoiceModalSource] = useState('AUTO');
    const [voiceModalText, setVoiceModalText] = useState('');
    const [voiceModalUrlMetaIndex, setVoiceModalUrlMetaIndex] = useState(undefined);
    const [showVoiceModal, setShowVoiceModal] = useState(false);
    const voiceModalSessionRef = React.useRef(0);
    const [isTitleEditing, setIsTitleEditing] = useState(false);
    const [dropzoneFiles, setDropzoneFiles] = useState(undefined);
    const [showActionMenu, setShowActionMenu] = useState(false);
    const [sharingMessage, setSharingMessage] = useState(null);
    const [showVersionHistoryModal, setShowVersionHistoryModal] = useState(false);
    const [showCreditsRequiredModal, setShowCreditsRequiredModal] = useState(false);
    const [creditsRequiredDescription, setCreditsRequiredDescription] = useState(
        'This action uses Lucky Credits. Add Lucky Credits to continue.'
    );
    const [isGeneratingTitleAudio, setIsGeneratingTitleAudio] = useState(false);
    const [isGeneratingNoteAudio, setIsGeneratingNoteAudio] = useState(false);
    const [isGeneratingArticleAudio, setIsGeneratingArticleAudio] = useState(false);
    const [selectedTtsVoice, setSelectedTtsVoice] = useState(getStoredTtsVoice);
    const serverVoices = useServerTtsVoices(showVoiceModal, selectedTtsVoice, setSelectedTtsVoice, TTS_VOICE_STORAGE_KEY);
    const [isNoteOverlayOpen, setIsNoteOverlayOpen] = useState(
        String(message?.previewMode || '').toUpperCase() === 'NOTE'
    );
    const [isNoteEditing, setIsNoteEditing] = useState(false);
    const [noteEditorMode, setNoteEditorMode] = useState(NOTE_EDIT_MODES.RICH);
    const actionMenuRef = React.useRef(null);
    const actionButtonRef = React.useRef(null);
    const [actionMenuCoords, setActionMenuCoords] = React.useState({ top: 0, right: 0 });
    const titleInputRef = React.useRef(null);
    const isClosingRef = React.useRef(false); // Track if we're closing the message
    const noteAutoOpenedRef = React.useRef(null);
    const emptyNoteAutoEditRef = React.useRef(null);

    const isXBookmark = !suppliedContentOnly && (message?.id?.startsWith('x-') || message?.id?.startsWith('public:'));
    const isNoteMode = isNoteOverlayOpen;
    const [localBody, setLocalBody] = useState('');

    // Initialize/Load local body from localStorage
    React.useEffect(() => {
        if (!isXBookmark) {
            setLocalBody('');
            return;
        }

        const savedBody = normalizeMarkdownForStorage(
            localStorage.getItem(`luckynote_local_body_${message.id}`) || ''
        );
        setLocalBody(savedBody);
    }, [isXBookmark, message.id]);

    const handleLocalBodySave = (newBody) => {
        const normalizedBody = normalizeMarkdownForStorage(newBody || '');
        setLocalBody(normalizedBody);
        localStorage.setItem(`luckynote_local_body_${message.id}`, normalizedBody);
        
        // Dispatch custom event for cross-component synchronization
        window.dispatchEvent(new CustomEvent('localNoteUpdated', {
            detail: {
                messageId: message.id,
                localBody: normalizedBody
            }
        }));
    };

    // Listen for local note updates from other components (e.g., Main List View)
    React.useEffect(() => {
        if (!isXBookmark) return;

        const handleUpdate = (event) => {
            const { messageId, localBody: updatedBody } = event.detail || {};
            if (messageId === message.id) {
                setLocalBody(normalizeMarkdownForStorage(updatedBody || ''));
            }
        };

        window.addEventListener('localNoteUpdated', handleUpdate);
        return () => window.removeEventListener('localNoteUpdated', handleUpdate);
    }, [isXBookmark, message.id]);

    const handleNoteClick = (targetMessage = message) => {
        if (!targetMessage) {
            return;
        }

        setIsNoteEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);
        setIsNoteOverlayOpen(true);
    };

    const submitPreviewForm = React.useCallback(async () => {
        if (readOnly) return;
        const formApi = formikRef.current;
        if (!formApi) {
            console.debug('[MessagePreview] No form API available to flush');
            return;
        }

        console.debug('[MessagePreview] Flushing pending preview form changes');
        if (typeof formApi.submitForm === 'function') {
            await formApi.submitForm();
            return;
        }

        const submitResult = formApi.handleSubmit?.();
        if (submitResult && typeof submitResult.then === 'function') {
            await submitResult;
        }
    }, [lnT, readOnly]);

    const handleBackToMessage = React.useCallback(async (targetMessage = message) => {
        await submitPreviewForm();
        setIsNoteEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);
        setIsNoteOverlayOpen(false);

        if (!targetMessage) {
            return;
        }

        const clearedMessage = { ...targetMessage, previewMode: undefined };
        selectPreviewMessage(clearedMessage);
        setSelectedMessage(clearedMessage);
    }, [message, selectPreviewMessage, setSelectedMessage, submitPreviewForm]);

    const handleStartNoteEditing = React.useCallback(() => {
        if (readOnly) return;
        setShowActionMenu(false);
        setIsTitleEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);
        React.startTransition(() => {
            setIsNoteEditing(true);
        });
    }, [lnT, readOnly]);

    const newMessageRef = React.useRef(null);

    React.useImperativeHandle(
        forwardedRef,
        () => ({
            flushPendingChanges: submitPreviewForm
        }),
        [submitPreviewForm]
    );

    // custom hooks
    const { updateMessage } = useUpdateMessageMutation();
    const [deleteMessages] = useDeleteMessagesMutation();
    const [trashMessages] = useTrashMessagesMutation();
    const { restoreMessagesMutation } = useRestoreMessagesMutation();
    const { updateCheckboxes, spliceFromCheckboxes } = useUpdateCheckboxes();
    const { updateStarred, spliceStarred } = useUpdateStarred();
    const { Platform, PLATFORMS } = useCheckBrowser();

    const [copySharedMessageMutation, { loading: copySharedMessageLoading }] = useMutation(MUTATION.COPY_SHARED_MESSAGE);
    const [starMessageMutation] = useMutation(MUTATION.STAR_MESSAGE);
    const [unstarMessageMutation] = useMutation(MUTATION.UNSTAR_MESSAGE);
    const [addCheckboxMutation] = useMutation(MUTATION.ADD_CHECKBOX);
    const [removeCheckboxMutation] = useMutation(MUTATION.REMOVE_CHECKBOX);
    const [setMessageDueMutation, { loading: setMessageReminderLoading }] = useMutation(MUTATION.SET_MESSAGE_DUE);
    const [clearMessageDueMutation] = useMutation(MUTATION.CLEAR_MESSAGE_DUE);
    const [textToSpeechMutation] = useMutation(MUTATION.TEXT_TO_SPEECH);

    const persistPreviewNoteBody = React.useCallback(
        async (targetMessage, values, setFieldValue, nextBody) => {
            const normalizedCurrentBody = normalizeMarkdownForStorage(values?.body || '');
            const normalizedNextBody = normalizeMarkdownForStorage(nextBody || '');

            if (normalizedCurrentBody === normalizedNextBody) {
                return;
            }

            setFieldValue('body', normalizedNextBody, false);

            if (isXBookmark) {
                handleLocalBodySave(normalizedNextBody);
                return;
            }

            const optimisticMessage = {
                ...targetMessage,
                body: normalizedNextBody,
                updatedAt: new Date().toISOString()
            };
            applyLocalMessageUpdate(optimisticMessage);

            const updatedMessage = await updateMessage(message, {
                ...values,
                body: normalizedNextBody
            });

            if (updatedMessage && !isClosingRef.current) {
                const mergedUpdated = {
                    ...targetMessage,
                    ...updatedMessage,
                    body: updatedMessage.body ?? normalizedNextBody
                };
                selectPreviewMessage(applyPreviewSelectionState(mergedUpdated));
            }
        },
        [lnT,
            applyLocalMessageUpdate,
            applyPreviewSelectionState,
            handleLocalBodySave,
            isXBookmark,
            message,
            selectPreviewMessage,
            updateMessage
        ]
    );

    const handleStopNoteEditing = React.useCallback(async explicitBody => {
        const nextBody = typeof explicitBody === 'string'
            ? normalizeMarkdownForStorage(explicitBody)
            : normalizeMarkdownForStorage(formikRef.current?.values?.body || '');

        setIsNoteEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);

        if (formikRef.current) {
            formikRef.current.setFieldValue('body', nextBody, false);
        }

        const target = newMessageRef.current || message;
        if (target) {
            await persistPreviewNoteBody(
                target,
                formikRef.current?.values || {},
                formikRef.current?.setFieldValue || (() => {}),
                nextBody
            );
        } else {
            await submitPreviewForm();
        }
    }, [message, persistPreviewNoteBody, submitPreviewForm]);

    const handlePreviewTaskCheckboxToggle = React.useCallback(
        async (targetMessage, values, setFieldValue, taskIndex, checked) => {
            if (readOnly) return;
            const nextBody = setMarkdownTaskCheckboxState(values?.body || '', taskIndex, checked);
            await persistPreviewNoteBody(targetMessage, values, setFieldValue, nextBody);
        },
        [lnT, readOnly, persistPreviewNoteBody]
    );

    const handlePreviewTitleTaskCheckboxToggle = React.useCallback(
        async (targetMessage, values, setFieldValue, taskIndex, checked) => {
            if (readOnly) return;
            const title = setMarkdownTaskCheckboxState(values?.title || '', taskIndex, checked);
            if (title === values?.title) return;
            setFieldValue('title', title, false);
            applyLocalMessageUpdate({ ...targetMessage, title, updatedAt: new Date().toISOString() });
            const updatedMessage = await updateMessage(message, { ...values, title });
            if (updatedMessage && !isClosingRef.current) {
                const mergedUpdated = {
                    ...targetMessage,
                    ...updatedMessage,
                    body: updatedMessage.body ?? targetMessage?.body
                };
                selectPreviewMessage(applyPreviewSelectionState(mergedUpdated));
            }
        },
        [readOnly, applyLocalMessageUpdate, updateMessage, message, selectPreviewMessage, applyPreviewSelectionState]
    );
    
    // Use downloadFile query to refresh S3 signed URLs
    const { refetch: refetchDownloadFile } = useQuery(QUERY.DOWNLOAD_FILE, { skip: true });

    // Skip query for temp/optimistic messages (they don't exist on server yet)
    const isTempMessage = message?.id?.startsWith('temp-') || message?.id?.startsWith('x-') || message?.id?.startsWith('public:');

    const { data: meData } = useQuery(QUERY.ME, {
        skip: suppliedContentOnly,
        errorPolicy: 'all'
    });

    const { data: textToSpeechFlagData, loading: textToSpeechFlagLoading } = useQuery(GET_FEATURE_FLAG, {
        variables: { name: 'text_to_speech' },
        skip: suppliedContentOnly,
        errorPolicy: 'all'
    });

    const { data: messageData } = useQuery(QUERY.GET_MESSAGE, {
        variables: {
            trackView: true,
            data: {
                id: message.id
            }
        },
        fetchPolicy: 'network-only',
        skip: suppliedContentOnly || isTempMessage // Shared views use only the authorized supplied content
    });
    const textToSpeechFlag = textToSpeechFlagData?.getFeatureFlag;
    const textToSpeechAccessDecision = React.useMemo(
        () => evaluateFeatureFlagForUser(textToSpeechFlag, meData?.me, { loading: textToSpeechFlagLoading }),
        [lnT, textToSpeechFlag, meData?.me, textToSpeechFlagLoading]
    );

    const formikRef = React.useRef();

    const [ignored, forceUpdate] = React.useReducer(x => x + 1, 0);

    React.useEffect(() => {
        if (!showActionMenu) {
            return undefined;
        }

        const handleOutsideClick = event => {
            if (
                actionMenuRef.current && !actionMenuRef.current.contains(event.target) &&
                actionButtonRef.current && !actionButtonRef.current.contains(event.target)
            ) {
                setShowActionMenu(false);
            }
        };

        const handleEscape = event => {
            if (event.key === 'Escape') {
                setShowActionMenu(false);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        document.addEventListener('keydown', handleEscape, true);

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('keydown', handleEscape, true);
        };
    }, [showActionMenu]);

    React.useEffect(() => {
        setIsTitleEditing(false);
        setIsNoteEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);
        setIsNoteOverlayOpen(String(message?.previewMode || '').toUpperCase() === 'NOTE');
        setLocalFolderOverride(null);
    }, [message?.id, message?.previewMode]);

    React.useEffect(() => {
        if (isNoteMode) {
            setIsTitleEditing(false);
            setShowActionMenu(false);
            return;
        }

        setIsNoteEditing(false);
        setNoteEditorMode(NOTE_EDIT_MODES.RICH);
    }, [isNoteMode]);

    React.useEffect(() => {
        if (!isNoteOverlayOpen) {
            return undefined;
        }

        const handleEscape = event => {
            if (event.key !== 'Escape' || event.defaultPrevented) {
                return;
            }
            if (isNoteEditing) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            handleBackToMessage();
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [isNoteOverlayOpen, isNoteEditing, handleBackToMessage]);

    React.useEffect(() => {
        if (!isTitleEditing || !titleInputRef.current) {
            return;
        }
        titleInputRef.current.focus();
        const titleLength = titleInputRef.current.value?.length || 0;
        titleInputRef.current.setSelectionRange(titleLength, titleLength);
    }, [isTitleEditing]);

    React.useEffect(() => {
        const openedAt = Date.now();
        customAnalytics.pageview('/message', 'MessageScreen');
        customAnalytics.messageview({
            'action': "Open",
            "msg_id": message.id
        }, message)
        return () => {
            customAnalytics.messageview({
                'action': "Close",
                "msg_id": message.id,
                "duration_seconds": Math.round((Date.now() - openedAt) / 1000)
            }, message)
        };
    }, []);

    React.useEffect(() => {
        // just so the titleRef can rerender
        if (messageContainerRef.current) {
            forceUpdate();
        }
    }, [messageContainerRef.current]);

    // Add effect to rebuild tooltips when needed
    React.useEffect(() => {
        ReactTooltip.rebuild();
        // Cleanup function to hide tooltips when component unmounts
        return () => {
            ReactTooltip.hide();
        };
    }, [messageData, isNoteMode]); // Rebuild when data changes or note mode toggles

    // getting the message from apollo cache

    const formatReminderDate = date => {
        if (!date) return 'N/A';
        const d = new Date(date);
        if (isNaN(d.getTime())) return 'N/A';
        return format(d, 'dd MMM yyyy, HH:mm');
    };

    const normalizeReminderDeliveryType = value => {
        const normalizedValue = String(value || '').trim().toLowerCase();
        return normalizedValue === 'email' ? 'EMAIL' : 'PUSH';
    };

    const formatReminderDeliveryTypeLabel = value =>
        normalizeReminderDeliveryType(value) === 'EMAIL' ? 'email' : 'notification';
    const getReminderLabelText = currentMessage => {
        const customReminderMessage = String(currentMessage?.reminderMessage || '').trim();
        return customReminderMessage ? customReminderMessage : 'Reminder';
    };

    const buildReminderNotificationId = currentMessage =>
        `${String(currentMessage?.id || 'message')}-${Date.now()}`;

    const openReminderPicker = currentMessage => {
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) return;
        setReminderPickerMessage(currentMessage);
    };

    const submitReminder = async ({ reminderAt, alerts, reminderMessage }) => {
        const currentMessage = reminderPickerMessage;
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) return;
        try {
            const response = await setMessageDueMutation({
                variables: {
                    input: {
                        messageId: currentMessage.id,
                        dueAt: reminderAt.toISOString(),
                        dueTimezone: getReminderDueTimezone(),
                        alerts,
                        reminderMessage,
                        installationId: REMINDER_WEB_INSTALLATION_ID
                    }
                }
            });

            const updatedMessage = response?.data?.setMessageDue;
            if (!updatedMessage?.id) {
                throw new Error('Reminder was not saved');
            }

            applyLocalMessageUpdate(updatedMessage);
            setReminderPickerMessage(null);
            showDefaultNotification(
                `set-reminder-${buildReminderNotificationId(currentMessage)}`,
                `Reminder set for ${formatReminderDate(updatedMessage.reminderAt)} via ${formatReminderDeliveryTypeLabel(updatedMessage.reminderDeliveryType)}`
            );
            customAnalytics.message_action({
                action: 'Set reminder',
                screen: 'MessageScreen'
            }, currentMessage);
            customAnalytics.reminder_action({
                'action': 'Set',
                'delivery_type': alerts?.[0]?.deliveryType,
                'lead_hours': Math.round((reminderAt.getTime() - Date.now()) / 3600000),
                'has_note': Boolean(String(reminderMessage || '').trim()),
                'alert_count': alerts?.length || 0,
                'source': 'MessageScreen'
            });
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToSaveReminder');
            showDefaultNotification(`set-reminder-error-${buildReminderNotificationId(currentMessage)}`, errorMessage);
        }
    };

    const clearReminder = async currentMessage => {
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt || !currentMessage?.reminderAt) return;

        try {
            const response = await clearMessageDueMutation({
                variables: {
                    messageId: currentMessage.id
                }
            });

            const updatedMessage = response?.data?.clearMessageDue;
            if (!updatedMessage?.id) {
                throw new Error('Reminder was not cleared');
            }

            applyLocalMessageUpdate(updatedMessage);
            showDefaultNotification(`clear-reminder-${buildReminderNotificationId(currentMessage)}`, 'Reminder cleared');
            customAnalytics.message_action({
                action: 'Clear reminder',
                screen: 'MessageScreen'
            }, currentMessage);
            customAnalytics.reminder_action({
                'action': 'Clear',
                'source': 'MessageScreen'
            });
        } catch (error) {
            const errorMessage = error?.message?.replace(/^GraphQL error:\s*/i, '') || lnT('messages:unableToClearReminder');
            showDefaultNotification(`clear-reminder-error-${buildReminderNotificationId(currentMessage)}`, errorMessage);
        }
    };

    const deletePermanently = () => {
        clearPreviewSelection();
        deleteMessages([message]);
    };

    const isTauri = isTauriEnvironment();

    const downloadImage = async (image) => {
        const fileName = image.displayName || image.name || 'image';
        const progressKey = `download_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const updateProgress = (percentage) => {
            // setUploadProgress expects the data object directly, not a function
            const currentProgress = uploadProgress || {};
            const newProgress = {
                ...currentProgress,
                [progressKey]: {
                    state: percentage < 100 ? 'pending' : 'done',
                    percentage,
                    type: 'image',
                    name: fileName
                }
            };
            setUploadProgress(newProgress);
        };
        
        try {
            updateProgress(10);
            
            const { url, public_id, displayName, name, size, cdn_url } = image;
            
            // Detect if this is a Cloudinary image
            const isCloudinaryImage = public_id && 
                                     (public_id.includes('cloudinary') || 
                                      url?.includes('cloudinary.com'));
            
            let downloadUrl;
            let fileDisplayName = displayName || name || 'image';
            
            if (isCloudinaryImage) {
                // For Cloudinary images, use Cloudinary URL (no refresh needed)
                updateProgress(30);
                downloadUrl = await cloudinaryCore.url(url);
            } else if (name) {
                // For S3 images, get fresh S3 signed URL using downloadFile query
                updateProgress(30);
                // Use image.name (S3 path) to get a fresh signed URL
                try {
                    const { data, error } = await refetchDownloadFile({
                        data: {
                            fileName: name  // S3 path
                        }
                    });
                    
                    if (error) {
                        throw error;
                    }
                    
                    if (data?.downloadFile?.__typename === 'S3File') {
                        downloadUrl = data.downloadFile.url;  // Fresh S3 signed URL
                    } else {
                        throw new Error('Failed to get download URL');
                    }
                } catch (refreshError) {
                    console.error('Error refreshing S3 URL, trying fallback:', refreshError);
                    // Fallback to cached URL (may be expired, but worth trying)
                    downloadUrl = image.thumbnail?.original || cdn_url || url;
                }
            } else {
                // No S3 path available, use cached URL
                downloadUrl = image.thumbnail?.original || cdn_url || url;
            }
            
            // Fetch as blob to handle cross-origin downloads properly
            updateProgress(50);
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            // Track download progress if Content-Length is available
            const contentLength = response.headers.get('content-length');
            if (contentLength && parseInt(contentLength, 10) > 0) {
                const total = parseInt(contentLength, 10);
                const reader = response.body.getReader();
                const chunks = [];
                let received = 0;
                
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                    received += value.length;
                    const progress = 50 + Math.floor((received / total) * 45); // 50-95%
                    updateProgress(Math.min(progress, 95));
                }
                
                const blob = new Blob(chunks);
                if (isTauri) {
                    const saved = await saveBlobWithTauri(blob, fileDisplayName);
                    if (!saved) {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                        return;
                    }
                } else {
                    downloadBlobInBrowser(blob, fileDisplayName);
                }
            } else {
                // No content-length, simulate progress
                updateProgress(60);
                await new Promise(resolve => setTimeout(resolve, 100));
                updateProgress(80);
                await new Promise(resolve => setTimeout(resolve, 100));
                const blob = await response.blob();
                if (isTauri) {
                    const saved = await saveBlobWithTauri(blob, fileDisplayName);
                    if (!saved) {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                        return;
                    }
                } else {
                    downloadBlobInBrowser(blob, fileDisplayName);
                }
            }
            
            // Mark as complete
            updateProgress(100);
            
            // Clear progress after a short delay
            setTimeout(() => {
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            }, 2000);
            
            customAnalytics.ui_action({
                'action': "Download Image",
                "size": size
            });
        } catch (error) {
            console.error('Error downloading image:', error);
            // Final fallback: try direct download link
            try {
                updateProgress(50);
                const fallbackUrl = image.thumbnail?.original || image.cdn_url || image.url;
                const fallbackResponse = await fetch(fallbackUrl);
                if (!fallbackResponse.ok) {
                    throw new Error(`HTTP error! status: ${fallbackResponse.status}`);
                }
                const fallbackBlob = await fallbackResponse.blob();
                if (isTauri) {
                    const saved = await saveBlobWithTauri(fallbackBlob, image.displayName || image.name || 'image');
                    if (!saved) {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                        return;
                    }
                } else {
                    downloadBlobInBrowser(fallbackBlob, image.displayName || image.name || 'image');
                }
                
                // Mark as complete
                updateProgress(100);
                setTimeout(() => {
                    const currentProgress = uploadProgress || {};
                    const { [progressKey]: removed, ...rest } = currentProgress;
                    setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                }, 2000);
            } catch (fallbackError) {
                console.error('Fallback download also failed:', fallbackError);
                // Remove failed download from progress
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            }
        }
    };

    const downloadFile = async (file) => {
        const fileName = resolveDownloadFileName(file.name, file.displayName);
        const progressKey = `download_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        const updateProgress = (percentage) => {
            // setUploadProgress expects the data object directly, not a function
            const currentProgress = uploadProgress || {};
            const newProgress = {
                ...currentProgress,
                [progressKey]: {
                    state: percentage < 100 ? 'pending' : 'done',
                    percentage,
                    type: 'file',
                    name: fileName
                }
            };
            setUploadProgress(newProgress);
        };
        
        try {
            updateProgress(20);
            
            const { url, displayName, name, size, cdn_url } = file;
            const fileDisplayName = resolveDownloadFileName(name, displayName);
            
            let downloadUrl;
            
            if (name) {
                // For S3 files, get fresh S3 signed URL using downloadFile query
                updateProgress(40);
                // Use file.name (S3 path) to get a fresh signed URL
                try {
                    const { data, error } = await refetchDownloadFile({
                        data: {
                            fileName: name  // S3 path
                        }
                    });
                    
                    if (error) {
                        throw error;
                    }
                    
                    if (data?.downloadFile?.__typename === 'S3File') {
                        downloadUrl = data.downloadFile.url;  // Fresh S3 signed URL
                    } else {
                        throw new Error('Failed to get download URL');
                    }
                } catch (refreshError) {
                    console.error('Error refreshing S3 URL, trying fallback:', refreshError);
                    // Fallback to CDN URL or cached S3 URL
                    downloadUrl = cdn_url || url;
                }
            } else {
                // No S3 path available, use CDN URL or cached URL
                downloadUrl = cdn_url || url;
            }
            
            // Fetch as blob to handle cross-origin downloads properly
            updateProgress(60);
            const response = await fetch(downloadUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            updateProgress(80);
            const blob = await response.blob();
            if (isTauri) {
                const saved = await saveBlobWithTauri(blob, fileDisplayName);
                if (!saved) {
                    const currentProgress = uploadProgress || {};
                    const { [progressKey]: removed, ...rest } = currentProgress;
                    setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                    return;
                }
            } else {
                downloadBlobInBrowser(blob, fileDisplayName);
            }
            
            // Mark as complete
            updateProgress(100);
            
            // Clear progress after a short delay
            setTimeout(() => {
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            }, 2000);
            
            customAnalytics.ui_action({
                'action': "Download File",
                "size": size
            });
        } catch (error) {
            console.error('Error downloading file:', error);
            // Final fallback: try direct download link
            try {
                updateProgress(50);
                const isImageFile = file.type?.includes('image');
                const fallbackUrl = isImageFile
                    ? (file.thumbnail?.original || file.cdn_url || file.url)
                    : (file.cdn_url || file.url);
                const fallbackResponse = await fetch(fallbackUrl);
                if (!fallbackResponse.ok) {
                    throw new Error(`HTTP error! status: ${fallbackResponse.status}`);
                }
                const fallbackBlob = await fallbackResponse.blob();
                if (isTauri) {
                    const saved = await saveBlobWithTauri(
                        fallbackBlob,
                        resolveDownloadFileName(file.name, file.displayName)
                    );
                    if (!saved) {
                        const currentProgress = uploadProgress || {};
                        const { [progressKey]: removed, ...rest } = currentProgress;
                        setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                        return;
                    }
                } else {
                    downloadBlobInBrowser(fallbackBlob, resolveDownloadFileName(file.name, file.displayName));
                }
                
                // Mark as complete
                updateProgress(100);
                setTimeout(() => {
                    const currentProgress = uploadProgress || {};
                    const { [progressKey]: removed, ...rest } = currentProgress;
                    setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
                }, 2000);
            } catch (fallbackError) {
                console.error('Fallback download also failed:', fallbackError);
                // Remove failed download from progress
                const currentProgress = uploadProgress || {};
                const { [progressKey]: removed, ...rest } = currentProgress;
                setUploadProgress(Object.keys(rest).length > 0 ? rest : undefined);
            }
        }
    };

    const executeTrash = () => {
        clearPreviewSelection();
        trashMessages([message]);
        showDefaultNotification(message.id, 'Message moved to trash');
        if (onMessageTrashed) {
            onMessageTrashed(message);
        }
    };

    const trashAll = () => {
        if (message.reminderAt) {
            setShowReminderTrashWarning(true);
            return;
        }
        executeTrash();
    };

    const restoreMessageMutation = () => {
        clearPreviewSelection();
        restoreMessagesMutation([message]);
    };

    const copyTextFallback = text => {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.top = '-9999px';
        textarea.style.left = '-9999px';
        textarea.setAttribute('readonly', '');
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    };

    const copyTextToClipboard = async textToCopy => {
        if (!textToCopy) {
            return;
        }

        try {
            if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(textToCopy);
            } else {
                copyTextFallback(textToCopy);
            }
            showCopyToClipboardNotification();
        } catch (error) {
            try {
                copyTextFallback(textToCopy);
                showCopyToClipboardNotification();
            } catch (fallbackError) {
                // Ignore copy failures in preview action
            }
        }
    };

    const closeActionMenu = () => {
        setShowActionMenu(false);
    };

    const openMoveMessageModal = currentMessage => {
        if (disableMoveMessage || !getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) {
            return;
        }
        setMoveMessageTarget(currentMessage);
        closeActionMenu();
        setShowMoveFoldersModal(true);
    };

    const openVersionHistoryModal = currentMessage => {
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) {
            return;
        }
        closeActionMenu();
        setShowVersionHistoryModal(true);
    };

    const searchEntityMention = entity => {
        const searchTerm = String(entity?.name || '').trim();
        if (!searchTerm || typeof window === 'undefined') {
            return;
        }

        const searchEngine = getStoredSearchEngine();
        const searchUrl = getSearchUrl(searchTerm, searchEngine);
        openExternalUrl(searchUrl);

        customAnalytics.ui_action({
            action: 'Entity Mention Search',
            screen: 'MessagePreview',
            entity_type: entity?.type || 'other',
            engine: searchEngine?.name || 'default'
        });
    };

    const lookUpMessageTitle = currentMessage => {
        const searchTerm = String(currentMessage?.title || '').trim();
        if (!searchTerm || typeof window === 'undefined') {
            return;
        }

        const searchEngine = getStoredSearchEngine();
        const searchUrl = getSearchUrl(searchTerm, searchEngine);
        openExternalUrl(searchUrl);

        customAnalytics.ui_action({
            action: 'Message Title Search',
            screen: 'MessagePreview',
            engine: searchEngine?.name || 'default'
        });
    };

    const handleAttachmentSelection = event => {
        const files = Array.from(event.target.files || []);
        event.target.value = '';
        if (files.length === 0) {
            return;
        }

        if (typeof onDropzoneFilesSelected === 'function') {
            onDropzoneFilesSelected(files, primaryDropTarget?.id || primaryDropTarget || null);
            return;
        }

        setDropzoneFiles(files);
    };

    const isServerBackedMessageId = id => /^[a-fA-F0-9]{24}$/.test(String(id || ''));

    const getMutationMessageId = currentMessage => (
        currentMessage?._resolvedServerId ||
        currentMessage?.rawMessage?._resolvedServerId ||
        (
            isServerBackedMessageId(currentMessage?.rawMessage?.id)
                ? currentMessage.rawMessage.id
                : null
        ) ||
        currentMessage?.id
    );

    const getIsServerBackedMessage = currentMessage => (
        !readOnly && isServerBackedMessageId(getMutationMessageId(currentMessage))
    );

    const getMarkdownExportFileName = currentMessage => {
        const title = String(currentMessage?.title || '').trim();
        const safeTitle = title
            .replace(/[\\/:*?"<>|]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 80)
            .replace(/\s/g, '-');

        if (safeTitle) {
            return `${safeTitle}.md`;
        }

        const createdAt = currentMessage?.createdAt ? new Date(currentMessage.createdAt) : new Date();
        const stamp = Number.isNaN(createdAt.getTime())
            ? 'note'
            : format(createdAt, 'yyyy-MM-dd-HHmm');

        return `note-${stamp}.md`;
    };

    const buildMarkdownExportContent = (currentMessage, values) => {
        const title = String(values?.title ?? currentMessage?.title ?? '').trim();
        const body = normalizeMarkdownForStorage(String(values?.body ?? currentMessage?.body ?? ''));

        if (title && body) {
            return `# ${title}\n\n${body}`;
        }

        if (title) {
            return `# ${title}`;
        }

        return body || '';
    };

    const exportNoteAsMarkdown = React.useCallback(
        async (currentMessage, values) => {
            const markdown = buildMarkdownExportContent(currentMessage, values);
            const fileName = getMarkdownExportFileName(currentMessage);

            try {
                const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
                if (isTauri) {
                    const saved = await saveBlobWithTauri(blob, fileName);
                    if (!saved) {
                        return;
                    }
                } else {
                    downloadBlobInBrowser(blob, fileName);
                }

                closeActionMenu();
                showDefaultNotification(currentMessage?.id || message?.id, 'Exported as Markdown');
            } catch (error) {
                closeActionMenu();
                showDefaultNotification(currentMessage?.id || message?.id, 'Failed to export Markdown');
            }
        },
        [lnT, isTauri, message?.id, showDefaultNotification]
    );

    const parseCreditsFromError = messageText => {
        const text = String(messageText || '');
        const requiredMatch = text.match(/Required:\s*([0-9]+(?:\.[0-9]+)?)/i);

        const required = requiredMatch ? Number(requiredMatch[1]) : null;

        return {
            required: Number.isFinite(required) ? required : null
        };
    };

    const {audioRef: voicePreviewAudioRef, playing: isVoicePreviewPlaying,
        loading: isPreviewingVoice, play: playVoiceSample, toggle: handlePreviewVoiceToggle,
        stop: cleanupVoicePreview, setPlaying: setIsVoicePreviewPlaying} = useFixedTtsSamplePreview({
        open: showVoiceModal, voice: selectedTtsVoice,
        onError: () => showDefaultNotification(message?.id, 'Failed to preview voice.')
    });

    const checkTextToSpeechAccess = React.useCallback(
        (currentMessage, { silent = false, suppressUpgradeModal = false } = {}) => {
            if (textToSpeechAccessDecision?.allowed) {
                return true;
            }

            if (textToSpeechAccessDecision?.requiresUpgrade) {
                if (!suppressUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.TEXT_TO_SPEECH,
                        upgradeType: 'personal'
                    });
                } else if (!silent) {
                    showDefaultNotification(currentMessage?.id || message?.id, 'Audio generation requires a plan upgrade.');
                }
                return false;
            }

            if (textToSpeechAccessDecision?.requiresTeamUpgrade) {
                if (!suppressUpgradeModal) {
                    openFeatureUpgradeModal({
                        preset: FEATURE_UPGRADE_MODAL_PRESET.TEXT_TO_SPEECH,
                        upgradeType: 'team'
                    });
                } else if (!silent) {
                    showDefaultNotification(currentMessage?.id || message?.id, 'Audio generation requires a team plan upgrade.');
                }
                return false;
            }

            if (silent) {
                return false;
            }

            const fallbackMessage = (() => {
                switch (textToSpeechAccessDecision?.reason) {
                case 'LOADING':
                    return 'Checking audio generation availability. Please try again.';
                case 'FLAG_DISABLED':
                    return 'Audio generation is currently disabled';
                case 'TEAM_CONTEXT_REQUIRED':
                    return 'Audio generation requires an active team workspace context.';
                case 'FLAG_UNAVAILABLE':
                case 'INVALID_CONFIGURATION':
                    return 'Audio generation is temporarily unavailable. Please retry in a moment.';
                default:
                    return 'Audio generation is currently unavailable.';
                }
            })();

            showDefaultNotification(currentMessage?.id || message?.id, fallbackMessage);
            return false;
        },
        [lnT, textToSpeechAccessDecision, showDefaultNotification, message?.id]
    );

    React.useEffect(() => {
        return () => {
            voiceModalSessionRef.current += 1;
            cleanupVoicePreview();
        };
    }, [cleanupVoicePreview]);

    const openVoiceModal = (currentMessage, source, textValue) => {
        const text = (textValue || '').trim();
        const isNoteSource = source === 'NOTE' || source === 'ARTICLE';
        const emptyMessage = source === 'ARTICLE'
            ? 'Extract the article first to generate audio.'
            : isNoteSource
              ? 'Add a note first to generate audio.'
              : 'Add a title first to generate audio.';
        if (!canGenerate || !getIsServerBackedMessage(currentMessage) || currentMessage?.deletedAt) {
            return;
        }

        if (!text) {
            showDefaultNotification(currentMessage.id, emptyMessage);
            return;
        }

        cleanupVoicePreview();
        voiceModalSessionRef.current += 1;
        setVoiceModalSourceMessage(currentMessage);
        setVoiceModalSource(source || 'AUTO');
        setVoiceModalText(textValue || '');
        setShowVoiceModal(true);
    };

    const closeVoiceModal = () => {
        voiceModalSessionRef.current += 1;
        cleanupVoicePreview();
        setShowVoiceModal(false);
        setVoiceModalSourceMessage(undefined);
        setVoiceModalSource('AUTO');
        setVoiceModalText('');
        setVoiceModalUrlMetaIndex(undefined);
    };

    const openMessageVoiceModal = (currentMessage, preferredSource = 'TITLE') => {
        const draft = formikRef.current?.values;
        const sourceMessage = {
            ...currentMessage,
            title: draft?.title ?? currentMessage?.title ?? '',
            body: draft?.body ?? (isXBookmark ? localBody : (currentMessage?.body ?? ''))
        };
        const source = String(preferredSource === 'NOTE' ? sourceMessage.body : sourceMessage.title).trim()
            ? preferredSource : preferredSource === 'NOTE' ? 'TITLE' : 'NOTE';
        openVoiceModal(sourceMessage, source, source === 'TITLE' ? sourceMessage.title : sourceMessage.body);
    };

    const appendTtsFailureToNote = React.useCallback(
        async (currentMessage, errorMessage) => {
            if (!getIsServerBackedMessage(currentMessage) || currentMessage?.deletedAt) {
                return;
            }
            if (String(message?.id || '') !== String(currentMessage?.id || '')) {
                return;
            }

            const normalizedError = String(errorMessage || 'Unknown error').trim();
            if (!normalizedError) {
                return;
            }

            const timestamp = format(new Date(), 'yyyy-MM-dd HH:mm');
            const failureLine = `[TTS failed ${timestamp}] ${normalizedError}`;
            const currentBody = String(currentMessage?.body || '').trimEnd();
            const nextBody = currentBody ? `${currentBody}\n\n${failureLine}` : failureLine;

            try {
                applyLocalMessageUpdate({
                    ...currentMessage,
                    body: nextBody,
                    updatedAt: new Date().toISOString()
                });
                const updated = await updateMessage(currentMessage, {
                    title: currentMessage?.title || '',
                    body: nextBody
                });
                if (updated) {
                    applyLocalMessageUpdate(updated);
                }
            } catch (appendError) {
                // Ignore note append errors; primary TTS error is already surfaced.
            }
        },
        [lnT, message?.id, updateMessage, applyLocalMessageUpdate]
    );

    const handleGenerateAudio = async (currentMessage, source, textValue, options = {}) => {
        if (!canGenerate) return;
        const text = (textValue || '').trim();
        const isNoteSource = source === 'NOTE' || source === 'ARTICLE';
        const selectedVoice = String(options?.voice || selectedTtsVoice || 'alloy').toLowerCase();
        if (!serverVoices.valid || !serverVoices.voices.some(item => item.id === selectedVoice)) return;
        const selectedStyle = TTS_DEFAULT_STYLE;
        cleanupVoicePreview();
        if (!getIsServerBackedMessage(currentMessage) || currentMessage?.deletedAt) {
            return false;
        }
        if (!text) {
            showDefaultNotification(
                currentMessage.id,
                source === 'ARTICLE'
                    ? 'Extract the article first to generate audio.'
                    : isNoteSource
                      ? 'Add a note first to generate audio.'
                      : 'Add a title first to generate audio.'
            );
            return false;
        }

        if (!checkTextToSpeechAccess(currentMessage)) {
            return false;
        }

        if (source === 'ARTICLE') {
            setIsGeneratingArticleAudio(true);
        } else if (isNoteSource) {
            setIsGeneratingNoteAudio(true);
        } else {
            setIsGeneratingTitleAudio(true);
        }

        try {
            const response = await textToSpeechMutation({
                variables: {
                    data: {
                        messageId: currentMessage.id,
                        source,
                        ...(Number.isInteger(options?.urlMetaIndex) ? { urlMetaIndex: options.urlMetaIndex } : {}),
                        voice: selectedVoice,
                        style: selectedStyle
                    }
                }
            });

            const result = response?.data?.textToSpeech;
            if (result?.__typename === 'TextToSpeechError') {
                const errorMessage = result.message || lnT('messages:failedToGenerateAudio');
                const isInsufficientCreditsError = errorMessage.includes('Insufficient Credits');
                if (isInsufficientCreditsError) {
                    const { required } = parseCreditsFromError(errorMessage);
                    setCreditsRequiredDescription(
                        required !== null
                            ? `This action needs ${required.toFixed(2)} Lucky Credits.`
                            : 'This action uses Lucky Credits. Add Lucky Credits to continue.'
                    );
                    setShowCreditsRequiredModal(true);
                }
                if (!isInsufficientCreditsError) {
                    showDefaultNotification(currentMessage.id, errorMessage.includes('Not enough AI usage') ? lnT('common:insufficientCreditsPleaseAddCreditsToContinue') : errorMessage);
                }
                void appendTtsFailureToNote(currentMessage, errorMessage);
                return false;
            }

            if (result?.__typename === 'TextToSpeechQueued') {
                const queuedState = String(result.state || '').toUpperCase();
                const successMessage =
                    queuedState === 'COMPLETED'
                        ? result.message || 'Audio generation already completed.'
                        : result.message || 'Audio generation started in the background.';
                showDefaultNotification(currentMessage.id, successMessage);
                customAnalytics.ui_action({
                    action: source === 'ARTICLE' ? 'Generate Audio from Article' : isNoteSource ? 'Generate Audio from Note' : 'Generate Audio from Title',
                    screen: 'MessageScreen'
                });
                return true;
            }

            const isCurrentMessageUpdated =
                result?.id &&
                String(result.id) === String(currentMessage.id);

            if (result?.id) {
                if (isCurrentMessageUpdated) {
                    applyLocalMessageUpdate(result);
                } else {
                    emitMessageUpdate(result);
                }
            }

            showDefaultNotification(
                currentMessage.id,
                isCurrentMessageUpdated ? 'Audio added to message.' : 'Audio message created.'
            );
            customAnalytics.ui_action({
                action: source === 'ARTICLE' ? 'Generate Audio from Article' : isNoteSource ? 'Generate Audio from Note' : 'Generate Audio from Title',
                screen: 'MessageScreen'
            });
            return true;
        } catch (error) {
            const graphQLError = error?.graphQLErrors?.[0]?.message;
            const errorMessage = graphQLError || error?.message || lnT('messages:failedToGenerateAudio');
            const isInsufficientCreditsError = errorMessage.includes('Insufficient Credits');
            if (isInsufficientCreditsError) {
                const { required } = parseCreditsFromError(errorMessage);
                setCreditsRequiredDescription(
                    required !== null
                        ? `This action needs ${required.toFixed(2)} Lucky Credits.`
                        : 'This action uses Lucky Credits. Add Lucky Credits to continue.'
                );
                setShowCreditsRequiredModal(true);
            }
            if (!isInsufficientCreditsError) {
                showDefaultNotification(currentMessage.id, errorMessage.includes('Not enough AI usage') ? lnT('common:insufficientCreditsPleaseAddCreditsToContinue') : errorMessage);
            }
            void appendTtsFailureToNote(currentMessage, errorMessage);
            return false;
        } finally {
            if (source === 'ARTICLE') {
                setIsGeneratingArticleAudio(false);
            } else if (isNoteSource) {
                setIsGeneratingNoteAudio(false);
            } else {
                setIsGeneratingTitleAudio(false);
            }
        }
    };

    const handleGenerateFromVoiceModal = async () => {
        const currentMessage = voiceModalSourceMessage;
        if (!currentMessage) {
            closeVoiceModal();
            return;
        }

        const source = voiceModalSource;
        const text = voiceModalText;
        const voice = selectedTtsVoice;
        const style = TTS_DEFAULT_STYLE;

        const session = voiceModalSessionRef.current;
        const wasGenerated = await handleGenerateAudio(currentMessage, source, text, {
            voice,
            style,
            urlMetaIndex: voiceModalUrlMetaIndex
        });
        if (wasGenerated && session === voiceModalSessionRef.current) {
            closeVoiceModal();
        }
    };

    const handleToggleTask = async currentMessage => {
        if (readOnly) return;
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) return;
        const mutationMessageId = getMutationMessageId(currentMessage);
        closeActionMenu();

        if (!currentMessage.isCheckbox) {
            const optimisticMessage = {
                ...currentMessage,
                isCheckbox: true,
                isChecked: false
            };
            applyLocalMessageUpdate(optimisticMessage);

            try {
                const response = await addCheckboxMutation({
                    variables: { data: { messageId: mutationMessageId } },
                    optimisticResponse: {
                        addCheckbox: {
                            ...currentMessage,
                            isCheckbox: true,
                            isChecked: false,
                            __typename: 'Message'
                        }
                    },
                    update: async (_, { data }) => {
                        const updated = data?.addCheckbox;
                        if (updated) {
                            updateCheckboxes([updated]);
                            if (typeof window !== 'undefined') {
                                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updated } }));
                            }
                        }
                    }
                });

                const updatedMessage = response?.data?.addCheckbox;
                if (updatedMessage) {
                    applyLocalMessageUpdate(updatedMessage);
                }
            } catch (error) {
                // Ignore mutation errors in preview actions
            }
            return;
        }

        const optimisticMessage = {
            ...currentMessage,
            isCheckbox: false,
            isChecked: false
        };
        applyLocalMessageUpdate(optimisticMessage);

        try {
            const response = await removeCheckboxMutation({
                variables: { data: { messageId: mutationMessageId } },
                optimisticResponse: {
                    removeCheckbox: {
                        ...currentMessage,
                        isCheckbox: false,
                        isChecked: false,
                        __typename: 'Message'
                    }
                },
                update: async (_, { data }) => {
                    const updated = data?.removeCheckbox;
                    if (updated) {
                        spliceFromCheckboxes([updated]);
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updated } }));
                        }
                    }
                }
            });

            const updatedMessage = response?.data?.removeCheckbox;
            if (updatedMessage) {
                applyLocalMessageUpdate(updatedMessage);
            }
        } catch (error) {
            // Ignore mutation errors in preview actions
        }
    };

    const handleToggleStar = async currentMessage => {
        if (!getIsServerBackedMessage(currentMessage) || currentMessage.deletedAt) return;
        closeActionMenu();

        if (!currentMessage.isStarred) {
            const optimisticMessage = {
                ...currentMessage,
                isStarred: true
            };
            applyLocalMessageUpdate(optimisticMessage);

            try {
                const response = await starMessageMutation({
                    variables: { data: { messageId: currentMessage.id } },
                    optimisticResponse: {
                        starMessage: {
                            ...currentMessage,
                            isStarred: true,
                            __typename: 'Message'
                        }
                    },
                    update: async (_, { data }) => {
                        const updated = data?.starMessage;
                        if (updated) {
                            updateStarred([updated]);
                            if (typeof window !== 'undefined') {
                                window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updated } }));
                            }
                        }
                    }
                });

                const updatedMessage = response?.data?.starMessage;
                if (updatedMessage) {
                    applyLocalMessageUpdate(updatedMessage);
                }
            } catch (error) {
                // Ignore mutation errors in preview actions
            }
            return;
        }

        const optimisticMessage = {
            ...currentMessage,
            isStarred: false
        };
        applyLocalMessageUpdate(optimisticMessage);

        try {
            const response = await unstarMessageMutation({
                variables: { data: { messageId: currentMessage.id } },
                optimisticResponse: {
                    unstarMessage: {
                        ...currentMessage,
                        isStarred: false,
                        __typename: 'Message'
                    }
                },
                update: async (_, { data }) => {
                    const updated = data?.unstarMessage;
                    if (updated) {
                        spliceStarred([updated]);
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent('messageUpdated', { detail: { message: updated } }));
                        }
                    }
                }
            });

            const updatedMessage = response?.data?.unstarMessage;
            if (updatedMessage) {
                applyLocalMessageUpdate(updatedMessage);
            }
        } catch (error) {
            // Ignore mutation errors in preview actions
        }
    };

    const closeMessage = async () => {
        if (isNoteMode) {
            console.debug('[MessagePreview] Closing while note mode is open');
            await handleBackToMessage();
        } else {
            // Mark that we're closing to prevent reopening after save
            isClosingRef.current = true;
            
            // Close immediately to prevent any race conditions
            if (onClose) {
                onClose();
            } else {
                clearPreviewSelection();
            }
            
            // Save changes after closing (don't await - let it happen in background)
            // This ensures the preview closes immediately, but changes still save
            if (formikRef.current && formikRef.current.handleSubmit) {
                const submitResult = formikRef.current.handleSubmit();
                // handleSubmit may return a Promise or undefined
                if (submitResult && typeof submitResult.catch === 'function') {
                    submitResult.catch(error => {
                        // Ignore errors during save - preview is already closed
                    });
                }
            }
        }
    };

    const renderPreviewActionMenu = (currentMessage, { showExportMarkdown = false, exportValues = null } = {}) => {
        const showMutationActions = getIsServerBackedMessage(currentMessage);
        const canSaveSharedCopy = Boolean(
            accessFolder?.owner && meData?.me?.id &&
            String(accessFolder.owner) !== String(meData.me.id) &&
            !accessFolder.publishedToken && !currentMessage?.deletedAt &&
            /^[a-fA-F0-9]{24}$/.test(getMutationMessageId(currentMessage))
        );
        const canMoveMessage = showMutationActions && !disableMoveMessage && !currentMessage?.deletedAt;
        const canShowInFolder = !currentMessage?.deletedAt && typeof onShowInFolder === 'function' && Boolean(currentMessage?.folder);
        const isMac = Platform === PLATFORMS.MACINTOSH;
        const moveShortcutLabel = isMac ? lnT('messages:j') : lnT('messages:ctrlJ');
        const trashShortcutLabel = isMac ? '⌘⌫' : lnT('messages:ctrl_b3a772b4');

        return (
            <ActionMenuContainer>
                <ActionButton
                    ref={actionButtonRef}
                    role="button"
                    tabIndex={0}
                    aria-label={lnT('folders:moreActions')}
                    onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.currentTarget.click();
                        }
                    }}
                    onClick={event => {
                        event.stopPropagation();
                        if (!showActionMenu && actionButtonRef.current) {
                            const rect = actionButtonRef.current.getBoundingClientRect();
                            setActionMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                        }
                        setShowActionMenu(previous => !previous);
                    }}
                >
                    <FiMoreVertical size={16} color={'rgba(255,255,255,.8)'} />
                </ActionButton>
                {showActionMenu ? ReactDOM.createPortal(
                    <ActionMenu
                        ref={actionMenuRef}
                        $zIndex={actionMenuZIndex}
                        style={{ position: 'fixed', top: actionMenuCoords.top, right: actionMenuCoords.right }}
                    >
                                        {canMoveMessage ? (
                                            <ActionMenuItem
                                                onClick={() => {
                                                    openMoveMessageModal(currentMessage);
                                                }}
                                            >
                                                <ActionMenuIcon>
                                                    <FiFolder size={14} />
                                                </ActionMenuIcon>{lnT('common:moveTo')}<ActionMenuShortcut>{moveShortcutLabel}</ActionMenuShortcut>
                                            </ActionMenuItem>
                                        ) : null}
                        {canSaveSharedCopy ? (
                            <ActionMenuItem
                                disabled={copySharedMessageLoading}
                                onClick={async () => {
                                    if (copySharedMessageLoading) return;
                                    try {
                                        const result = await copySharedMessageMutation({ variables: { data: {
                                            messageId: getMutationMessageId(currentMessage)
                                        } } });
                                        if (!result?.data?.copySharedMessage?.message?.id) throw new Error(lnT('messages:unableToSaveACopy'));
                                        setShowActionMenu(false);
                                        showDefaultNotification(`copy-shared-message-${currentMessage.id}`, lnT('folders:savedToYourInboxAsAnEditable'));
                                    } catch (error) {
                                        showDefaultNotification(`copy-shared-message-error-${currentMessage.id}`, error?.message || lnT('messages:unableToSaveACopy'));
                                    }
                                }}
                            >
                                <FiCopy size={14} />
                                {copySharedMessageLoading ? lnT('messages:savingCopy') : lnT('messages:saveToMyInbox')}
                            </ActionMenuItem>
                        ) : null}
                        {canGenerate && !currentMessage.deletedAt && getIsServerBackedMessage(currentMessage) &&
                            Boolean(meData?.me?.id) && String(currentMessage.owner?.id || currentMessage.owner) === String(meData?.me?.id) ? (
                            <ActionMenuItem onClick={() => { setShowActionMenu(false); setSharingMessage(currentMessage); }}>
                                <ActionMenuIcon><LuMessageSquareShare size={14} /></ActionMenuIcon>{lnT('common:shareAndPublish')}
                            </ActionMenuItem>
                        ) : null}
                        {showMutationActions ? (
                            <>
                                {!currentMessage.deletedAt ? (
                                    <>
                                        <ActionMenuItem
                                            onClick={() => {
                                                handleToggleTask(currentMessage);
                                            }}
                                        >
                                            <ActionMenuIcon>
                                                <FiCheckSquare size={14} />
                                            </ActionMenuIcon>
                                            {!currentMessage.isCheckbox ? lnT('messages:addTask') : lnT('messages:untask')}
                                        </ActionMenuItem>
                                        <ActionMenuItem
                                            onClick={() => {
                                                handleToggleStar(currentMessage);
                                            }}
                                        >
                                            <ActionMenuIcon>
                                                <FiStar size={14} />
                                            </ActionMenuIcon>
                                            {!currentMessage.isStarred ? lnT('messages:star') : lnT('messages:unstar')}
                                        </ActionMenuItem>
                                        {canShowInFolder ? (
                                            <ActionMenuItem
                                                onClick={() => {
                                                    closeActionMenu();
                                                    onShowInFolder(currentMessage);
                                                }}
                                            >
                                                <ActionMenuIcon>
                                                    <LuMessageSquareShare size={14} />
                                                </ActionMenuIcon>{lnT('messages:showInFolder_da478765')}</ActionMenuItem>
                                        ) : null}
                                        <ActionMenuItem
                                            onClick={() => {
                                                closeActionMenu();
                                                openReminderPicker(currentMessage);
                                            }}
                                        >
                                            <ActionMenuIcon>
                                                <FiBell size={14} />
                                            </ActionMenuIcon>
                                            {currentMessage?.reminderAt ? lnT('messages:changeReminder') : lnT('messages:setReminder')}
                                        </ActionMenuItem>
                                        {currentMessage?.reminderAt ? (
                                            <ActionMenuItem
                                                onClick={() => {
                                                    closeActionMenu();
                                                    void clearReminder(currentMessage);
                                                }}
                                            >
                                                <ActionMenuIcon>
                                                    <FiXCircle size={14} />
                                                </ActionMenuIcon>{lnT('messages:clearReminder')}</ActionMenuItem>
                                        ) : null}
                                        <ActionMenuItem
                                            onClick={() => {
                                                openVersionHistoryModal(currentMessage);
                                            }}
                                        >
                                            <ActionMenuIcon>
                                                <FiRotateCcw size={14} />
                                            </ActionMenuIcon>{lnT('messages:versionHistory')}</ActionMenuItem>
                                        <ActionMenuSeparator />
                                        <ActionMenuSeparator />
                        {canGenerate && getIsServerBackedMessage(currentMessage) && !currentMessage?.deletedAt ? (
                            <ActionMenuItem onClick={() => {
                                closeActionMenu();
                                openMessageVoiceModal(currentMessage, 'TITLE');
                            }}>
                                <ActionMenuIcon><IoMdPlay size={14} /></ActionMenuIcon>{lnT('messages:listen')}
                            </ActionMenuItem>
                        ) : null}
                                        <ActionMenuItem
                                            onClick={() => {
                                                closeActionMenu();
                                                trashAll();
                                            }}
                                        >
                                            <ActionMenuIcon destructive>
                                                <FiTrash2 size={14} />
                                            </ActionMenuIcon>
                                            <DestructiveActionLabel>{lnT('folders:trash')}</DestructiveActionLabel>
                                            <ActionMenuShortcut>{trashShortcutLabel}</ActionMenuShortcut>
                                        </ActionMenuItem>
                                    </>
                                ) : (
                                    <>
                                        <ActionMenuItem
                                            onClick={() => {
                                                closeActionMenu();
                                                restoreMessageMutation();
                                            }}
                                        >
                                            <ActionMenuIcon>
                                                <FiRotateCcw size={14} />
                                            </ActionMenuIcon>{lnT('messages:restore')}</ActionMenuItem>
                                        <ActionMenuItem
                                            onClick={() => {
                                                closeActionMenu();
                                                setShowDeleteModal(true);
                                            }}
                                        >
                                            <ActionMenuIcon destructive>
                                                <FiXCircle size={14} />
                                            </ActionMenuIcon>
                                            <DestructiveActionLabel>{lnT('folders:deletePermanently_280b10c8')}</DestructiveActionLabel>
                                        </ActionMenuItem>
                                    </>
                                )}
                                <ActionMenuSeparator />
                            </>
                        ) : null}
                        {showExportMarkdown ? (
                            <>
                                <ActionMenuItem
                                    onClick={() => {
                                        void exportNoteAsMarkdown(currentMessage, exportValues);
                                    }}
                                >
                                    <ActionMenuIcon>
                                        <FiDownload size={14} />
                                    </ActionMenuIcon>{lnT('messages:exportAsMd')}</ActionMenuItem>
                                <ActionMenuSeparator />
                            </>
                        ) : null}
                    </ActionMenu>,
                    document.body
                ) : null}
            </ActionMenuContainer>
        );
    };

    const renderNoteActionMenu = (currentMessage, values) => {
        return (
            <ActionMenuContainer>
                <ActionButton
                    ref={actionButtonRef}
                    onClick={event => {
                        event.stopPropagation();
                        if (!showActionMenu && actionButtonRef.current) {
                            const rect = actionButtonRef.current.getBoundingClientRect();
                            setActionMenuCoords({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                        }
                        setShowActionMenu(previous => !previous);
                    }}
                >
                    <FiMoreVertical size={16} color={'rgba(255,255,255,.8)'} />
                </ActionButton>
                {showActionMenu ? ReactDOM.createPortal(
                    <ActionMenu
                        ref={actionMenuRef}
                        $zIndex={actionMenuZIndex}
                        style={{ position: 'fixed', top: actionMenuCoords.top, right: actionMenuCoords.right }}
                    >
                        {!isNoteEditing ? (
                            <ActionMenuItem
                                onClick={() => {
                                    closeActionMenu();
                                    handleStartNoteEditing();
                                }}
                            >
                                <ActionMenuIcon>
                                    <FiEdit2 size={14} />
                                </ActionMenuIcon>{lnT('messages:editNote')}</ActionMenuItem>
                        ) : null}
                        {isNoteEditing && !readOnly ? (
                            <ActionMenuItem
                                onClick={() => {
                                    setNoteEditorMode(previous =>
                                        previous === NOTE_EDIT_MODES.RAW ? NOTE_EDIT_MODES.RICH : NOTE_EDIT_MODES.RAW
                                    );
                                }}
                            >
                                <ActionMenuIcon>
                                    <FiEdit2 size={14} />
                                </ActionMenuIcon>{lnT('messages:rawMarkdown')}<ActionMenuToggle
                                    $isActive={noteEditorMode === NOTE_EDIT_MODES.RAW}
                                    aria-hidden="true"
                                >
                                    <ActionMenuToggleKnob $isActive={noteEditorMode === NOTE_EDIT_MODES.RAW} />
                                </ActionMenuToggle>
                            </ActionMenuItem>
                        ) : null}
                        <ActionMenuItem
                            onClick={() => {
                                closeActionMenu();
                                copyTextToClipboard((values?.body || '').trim());
                            }}
                        >
                            <ActionMenuIcon>
                                <FiCopy size={14} />
                            </ActionMenuIcon>{lnT('messages:copyNote')}</ActionMenuItem>
                        <ActionMenuItem
                            onClick={() => {
                                void exportNoteAsMarkdown(currentMessage, values);
                            }}
                        >
                            <ActionMenuIcon>
                                <FiDownload size={14} />
                            </ActionMenuIcon>{lnT('messages:downloadMd')}</ActionMenuItem>
                        {canGenerate && getIsServerBackedMessage(currentMessage) && !currentMessage?.deletedAt ? (
                            <ActionMenuItem onClick={() => {
                                closeActionMenu();
                                openMessageVoiceModal(currentMessage, 'NOTE');
                            }}>
                                <ActionMenuIcon><IoMdPlay size={14} /></ActionMenuIcon>{lnT('messages:listen')}
                            </ActionMenuItem>
                        ) : null}
                        {getIsServerBackedMessage(currentMessage) && !currentMessage?.deletedAt ? (
                            <ActionMenuItem
                                onClick={() => {
                                    openVersionHistoryModal(currentMessage);
                                }}
                            >
                                <ActionMenuIcon>
                                    <FiRotateCcw size={14} />
                                </ActionMenuIcon>{lnT('messages:versionHistory')}</ActionMenuItem>
                        ) : null}
                    </ActionMenu>,
                    document.body
                ) : null}
            </ActionMenuContainer>
        );
    };

    const renderHeader = newMessage => {
        return (
            <HeaderContainer>
                <LeftSection>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {showCloseButton ? (
                            <div
                                data-tip={`${Platform === PLATFORMS.MACINTOSH ? '(⌘.)' : '(Ctr+.)'}`}
                                data-place="right"
                            >
                                <ActionButton
                                    role="button"
                                    tabIndex={0}
                                    aria-label={lnT('common:close')}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            closeMessage();
                                        }
                                    }}
                                    onClick={() => {
                                        closeMessage();
                                    }}
                                >
                                    <IoMdClose size={16} color={'rgba(255,255,255,.8)'} />
                                </ActionButton>
                            </div>
                        ) : null}
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            {isNoteMode ? (
                                <FolderTitle style={{ cursor: 'default', pointerEvents: 'none' }}>
                                    <span>{lnT('messages:note')}</span>
                                </FolderTitle>
                            ) : newMessage.folder ? (
                                <FolderTitle
                                    onClick={() => {
                                        if (disableMoveMessage) {
                                            return;
                                        }

                                        openMoveMessageModal(newMessage);
                                        customAnalytics.ui_action({
                                            "action": "Message Move Open",
                                            "screen": "MessageScreen"
                                        })
                                    }}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={event => {
                                        if (event.key === 'Enter' || event.key === ' ') {
                                            event.preventDefault();
                                            if (disableMoveMessage) {
                                                return;
                                            }

                                            openMoveMessageModal(newMessage);
                                        }
                                    }}
                                    title={disableMoveMessage ? newMessage.folder.title : lnT('messages:moveToFolder')}
                                >
                                    <span>{newMessage.folder.title || lnT('messages:message')}</span>
                                    {!disableMoveMessage ? <FiChevronDown size={15} /> : null}
                                </FolderTitle>
                            ) : suppliedContentOnly ? (
                                <FolderTitle style={{cursor:'default',pointerEvents:'none'}}>
                                    <span>{lnT('messages:message')}</span>
                                </FolderTitle>
                            ) : (
                                <div style={{ fontSize: 13, color: RED }}>{lnT('messages:deletedFolder')}</div>
                            )}
                        </div>
                    </div>
                </LeftSection>

                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {onOpenInApp ? (
                        <HeaderIconButton
                            type="button"
                            onClick={() => onOpenInApp(message)}
                            title={lnT('messages:openInApp')}
                            aria-label={lnT('messages:openInApp')}
                        >
                            <IoMdOpen size={17} />
                        </HeaderIconButton>
                    ) : null}
                    {!newMessage.deletedAt && newMessage.folder && typeof onShowInFolder === 'function' ? (
                        <HeaderIconButton
                            type="button"
                            onClick={() => onShowInFolder(newMessage)}
                            title={lnT('messages:showInFolder_da478765')}
                            aria-label={lnT('messages:showInFolder_da478765')}
                        >
                            <LuMessageSquareShare size={17} />
                        </HeaderIconButton>
                    ) : null}
                    {getIsServerBackedMessage(newMessage) && !newMessage.deletedAt ? (
                        <HeaderIconButton
                            type="button"
                            onClick={() => {
                                void handleToggleStar(newMessage);
                            }}
                            title={newMessage.isStarred ? lnT('messages:unstar') : lnT('messages:star')}
                            aria-label={newMessage.isStarred ? lnT('messages:unstarMessage') : lnT('messages:starMessage')}
                        >
                            <FiStar
                                size={17}
                                fill={newMessage.isStarred ? 'currentColor' : 'none'}
                            />
                        </HeaderIconButton>
                    ) : null}
                    {renderPreviewActionMenu(newMessage)}
                </div>
            </HeaderContainer>
        );
    };

    const renderMessageInfo = currentMessage => (
        <MessageInfoPlacement>
            <MessageInfoSection
                message={currentMessage}
                reminderDetails={currentMessage.reminderAt || currentMessage.reminderFiredAt ? (
                    <ActionMenuMeta>
                        {getReminderLabelText(currentMessage)}: {formatReminderDate(currentMessage.reminderAt || currentMessage.reminderFiredAt)}{lnT('messages:via')}{formatReminderDeliveryTypeLabel(currentMessage.reminderDeliveryType)}
                        {Array.isArray(currentMessage.alerts) && currentMessage.alerts.length > 1 ? moreAlertsLabel(currentMessage.alerts.length - 1) : ''}
                    </ActionMenuMeta>
                ) : null}
            />
        </MessageInfoPlacement>
    );

    const renderTopDownMetadata = currentMessage => {
        const reminderTimestamp = currentMessage?.reminderAt || currentMessage?.reminderFiredAt;
        const isFiredReminder = Boolean(!currentMessage?.reminderAt && currentMessage?.reminderFiredAt);
        if (!reminderTimestamp) {
            return null;
        }

        return (
            <ReminderMetadataCard
                $isFiredReminder={isFiredReminder}
                disabled={readOnly}
                type="button"
                onClick={() => openReminderPicker(currentMessage)}
            >
                <ReminderMetadataLabel>
                    {isFiredReminder ? <FiCheckCircle size={16} color={GREEN} /> : <FiBell size={16} />}
                    <span>{getReminderLabelText(currentMessage)}</span>
                </ReminderMetadataLabel>
                <ReminderMetadataValue $isFiredReminder={isFiredReminder}>
                    <span>{formatReminderDate(reminderTimestamp)}</span>
                    <FiArrowRight size={16} />
                </ReminderMetadataValue>
            </ReminderMetadataCard>
        );
    };

    const renderTopDownActions = (currentMessage, values) => {
        if (readOnly) return null;
        // const title = String(values?.title || currentMessage?.title || '').trim();
        const actions = [];

        if (!currentMessage?.isCheckbox) {
            actions.push({
                key: 'task',
                label: lnT('messages:task'),
                icon: <FiCheckSquare size={16} />,
                onClick: () => handleToggleTask(currentMessage)
            });
        }
        if (!readOnly && !currentMessage?.reminderAt && !currentMessage?.reminderFiredAt) {
            actions.push({
                key: 'reminder',
                label: lnT('messages:reminder'),
                icon: <FiBell size={16} />,
                onClick: () => openReminderPicker(currentMessage)
            });
        }
        // if (title) {
        //     actions.push({
        //         key: 'lookup',
        //         label: lnT('messages:lookUp'),
        //         icon: <FiSearch size={16} />,
        //         onClick: () => lookUpMessageTitle({ ...currentMessage, title })
        //     });
        // }
        if (getIsServerBackedMessage(currentMessage) && !disableMoveMessage && !currentMessage.deletedAt) {
            actions.push({
                key: 'move',
                label: lnT('messages:moveTo'),
                icon: <FiFolder size={16} />,
                onClick: () => openMoveMessageModal(currentMessage)
            });
        }

        if (!actions.length) return null;
        return (
            <TopDownActions aria-label={lnT('messages:messageActions')}>
                {actions.map(action => (
                    <TopDownActionButton
                        key={action.key}
                        type="button"
                        onClick={action.onClick}
                    >
                        {action.icon}
                        <span>{action.label}</span>
                    </TopDownActionButton>
                ))}
            </TopDownActions>
        );
    };

    const renderNoteHeader = (newMessage, values) => {
        return (
            <NoteOverlayHeader>
                <LeftSection>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div data-tip="(Esc)" data-place="right">
                            <ActionButton
                                onClick={event => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    handleBackToMessage(newMessage);
                                }}
                                title={lnT('messages:closeNote')}
                                aria-label={lnT('messages:closeNote')}
                            >
                                <IoMdClose size={16} color={'rgba(255,255,255,.8)'} />
                            </ActionButton>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 17, lineHeight: '21px', userSelect: 'none', color: WHITE }}>{lnT('messages:note')}</div>
                            {!newMessage.folder && !suppliedContentOnly ? (
                                <div style={{ marginTop: 4, fontSize: 12, color: RED }}>{lnT('messages:deletedFolder')}</div>
                            ) : null}
                        </div>
                    </div>
                </LeftSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {isNoteEditing && !readOnly ? (
                        <InlineRawToggleButton
                            type="button"
                            onClick={event => {
                                event.preventDefault();
                                event.stopPropagation();
                                setNoteEditorMode(previous =>
                                    previous === NOTE_EDIT_MODES.RAW ? NOTE_EDIT_MODES.RICH : NOTE_EDIT_MODES.RAW
                                );
                            }}
                            aria-pressed={noteEditorMode === NOTE_EDIT_MODES.RAW}
                            title={lnT('messages:toggleRawMarkdown')}
                        >
                            <InlineRawToggleLabel>{lnT('messages:raw')}</InlineRawToggleLabel>
                            <ActionMenuToggle
                                $isActive={noteEditorMode === NOTE_EDIT_MODES.RAW}
                                aria-hidden="true"
                            >
                                <ActionMenuToggleKnob $isActive={noteEditorMode === NOTE_EDIT_MODES.RAW} />
                            </ActionMenuToggle>
                        </InlineRawToggleButton>
                    ) : null}
                    {renderNoteActionMenu(newMessage, values)}
                </div>
            </NoteOverlayHeader>
        );
    };

    React.useEffect(() => {
        if (!message?.id) {
            return;
        }

        // Auto-open note context only once per selected message.
        const shouldOpenNoteOnLoadContext =
            message?.loadContext === true &&
            (message?.openNoteOnLoadContext === true ||
                String(message?.previewMode || '').toUpperCase() === 'NOTE');

        if (shouldOpenNoteOnLoadContext && noteAutoOpenedRef.current !== message.id) {
            noteAutoOpenedRef.current = message.id;
            handleNoteClick(message);
        }
    }, [message?.id, message?.loadContext, message?.openNoteOnLoadContext, message?.previewMode]);

    useEffect(() => {
        if (dropzoneFiles) {
            // Instead of handling upload directly, just pass the files to PluginManager
            setDropzoneFiles(dropzoneFiles);
        }
    }, [dropzoneFiles]);

    // Pre-populate preview with list message data while fresh data loads
    // and prefer local state when we have a newer optimistic move/update.
    // Apollo can retain the previous query result while a request for a newly
    // selected message is in flight. Never merge that stale result into this
    // preview: it may otherwise render another message's URL metadata,
    // artifacts, or note body for a moment.
    const fetchedCandidate = messageData?.getMessage;
    const fetchedMessage =
        fetchedCandidate && String(fetchedCandidate.id || '') === String(message?.id || '')
            ? fetchedCandidate
            : null;
    const hasFolderMismatch =
        Boolean(message?.folder?.id) &&
        Boolean(fetchedMessage?.folder?.id) &&
        message.folder.id !== fetchedMessage.folder.id;
    const localUpdatedAt = Date.parse(message?.updatedAt || 0) || 0;
    const fetchedUpdatedAt = Date.parse(fetchedMessage?.updatedAt || 0) || 0;
    const shouldPreferLocalMessage = hasFolderMismatch || localUpdatedAt > fetchedUpdatedAt;
    const getUrlMetaKey = meta => meta?.url || meta?.canonical_url || meta?.title || null;
    const hasArrayValue = value => Array.isArray(value) && value.length > 0;
    const preferArrayValue = (primary, fallback) => hasArrayValue(primary) ? primary : fallback;
    const mergeUrlMetaEntry = (fetchedMeta, localMeta) => {
        if (!fetchedMeta) return localMeta;
        if (!localMeta) return fetchedMeta;

        return {
            ...localMeta,
            ...fetchedMeta,
            thumbnail: fetchedMeta.thumbnail || localMeta.thumbnail,
            author_image_thumbnail: fetchedMeta.author_image_thumbnail || localMeta.author_image_thumbnail,
            image_cdn_url: fetchedMeta.image_cdn_url || localMeta.image_cdn_url,
            author_image_cdn_url: fetchedMeta.author_image_cdn_url || localMeta.author_image_cdn_url,
            media_cdn_url: fetchedMeta.media_cdn_url || localMeta.media_cdn_url,
            media_audio_url: fetchedMeta.media_audio_url || localMeta.media_audio_url,
            media_hls_url: fetchedMeta.media_hls_url || localMeta.media_hls_url,
            gallery: preferArrayValue(fetchedMeta.gallery, localMeta.gallery),
            gallery_images: preferArrayValue(fetchedMeta.gallery_images, localMeta.gallery_images)
        };
    };
    const mergeUrlMeta = (fetchedUrlMeta, localUrlMeta) => {
        const fetchedList = Array.isArray(fetchedUrlMeta) ? fetchedUrlMeta : [];
        const localList = Array.isArray(localUrlMeta) ? localUrlMeta : [];

        if (!fetchedList.length) return localList.length ? localList : undefined;
        if (!localList.length) return fetchedList;

        const usedLocalIndexes = new Set();
        const mergedList = fetchedList.map((fetchedMeta, index) => {
            const fetchedKey = getUrlMetaKey(fetchedMeta);
            const matchingLocalIndex = fetchedKey
                ? localList.findIndex((localMeta, localIndex) =>
                    !usedLocalIndexes.has(localIndex) && getUrlMetaKey(localMeta) === fetchedKey
                  )
                : -1;
            const localIndex = matchingLocalIndex >= 0 ? matchingLocalIndex : index;
            const localMeta = !usedLocalIndexes.has(localIndex) ? localList[localIndex] : undefined;

            if (localMeta) {
                usedLocalIndexes.add(localIndex);
            }

            return mergeUrlMetaEntry(fetchedMeta, localMeta);
        });

        localList.forEach((localMeta, index) => {
            if (!usedLocalIndexes.has(index)) {
                mergedList.push(localMeta);
            }
        });

        return mergedList;
    };
    const resolvedUrlMeta = mergeUrlMeta(fetchedMessage?.urlMeta, message?.urlMeta);
    const fetchedOrLocalMessage = suppliedContentOnly || isTempMessage
        ? message
        : shouldPreferLocalMessage
            ? { ...(fetchedMessage || {}), ...(message || {}), urlMeta: resolvedUrlMeta }
            : {
                ...(fetchedMessage || message),
                urlMeta: resolvedUrlMeta,
                // AI-analysis fields: protect against fetchedMessage missing them
                // (e.g. GET_MESSAGE returned before AI analysis completed, or
                // fetchedMessage.ocrText is null while message prop has the value)
                ocrText: fetchedMessage?.ocrText ?? message?.ocrText ?? null,
                caption: fetchedMessage?.caption ?? message?.caption ?? null,
                keywords: fetchedMessage?.keywords ?? message?.keywords ?? null,
                entities: fetchedMessage?.entities ?? message?.entities ?? null,
                artifactIds: fetchedMessage?.artifactIds ?? message?.artifactIds ?? null,
                artifacts: fetchedMessage?.artifacts ?? message?.artifacts ?? null,
                structuredContent: fetchedMessage?.structuredContent ?? message?.structuredContent ?? null,
                structuredContentState: fetchedMessage?.structuredContentState ?? message?.structuredContentState ?? null
              };
    const newMessage =
        localFolderOverride?.messageId === String(fetchedOrLocalMessage?.id || '')
            ? { ...fetchedOrLocalMessage, folder: localFolderOverride.folder }
            : fetchedOrLocalMessage;
    newMessageRef.current = newMessage;
    React.useEffect(() => {
        if (!isNoteMode || isNoteEditing || !newMessage?.id) {
            return;
        }

        const autoEditKey = `${newMessage.id}:note`;
        if (emptyNoteAutoEditRef.current === autoEditKey) {
            return;
        }

        const normalizedNoteBody = isXBookmark
            ? normalizeMarkdownForStorage(localBody || '')
            : normalizeMarkdownForStorage(newMessage?.body || '');

        if (String(normalizedNoteBody || '').trim()) {
            return;
        }

        emptyNoteAutoEditRef.current = autoEditKey;
        handleStartNoteEditing();
    }, [handleStartNoteEditing, isNoteEditing, isNoteMode, isXBookmark, localBody, newMessage]);
    const isGeneratingAudio = isGeneratingTitleAudio || isGeneratingNoteAudio || isGeneratingArticleAudio;
    const canGenerateFromVoiceModal = canGenerate && serverVoices.valid && Boolean(String(voiceModalText || '').trim());
    const voiceModalExcerpt = React.useMemo(() => {
        const normalizedText = String(voiceModalText || '')
            .replace(/\s+/g, ' ')
            .trim();

        if (!normalizedText) {
            return 'No text available.';
        }

        const maxExcerptLength = 220;
        if (normalizedText.length <= maxExcerptLength) {
            return normalizedText;
        }

        return `${normalizedText.slice(0, maxExcerptLength).trimEnd()}...`;
    }, [lnT, voiceModalText]);
    
    if (newMessage) {

        return (
            <React.Fragment>
                <MyDropzone
                    title={dropzoneTitle}
                    dropTargets={dropTargets}
                    primaryDropTarget={primaryDropTarget}
                    filesSelected={(files, dropTarget) => {
                        if (readOnly) return undefined;
                        if (typeof onDropzoneFilesSelected === 'function') {
                            return onDropzoneFilesSelected(files, dropTarget);
                        }

                        if (files) {
                            setDropzoneFiles(files);
                        }
                        return undefined;
                    }}
                    disabled={readOnly || showDeletedMessages}
                >
                    <Hotkeys
                        keyName="command+Backspace, ctrl+Backspace, command+., ctrl+., command+J, ctrl+J, command+j, ctrl+j, delete"
                        onKeyDown={(keyName, e, handle) => {
                            // setShowFoldersSearch(true);
                            if (
                                !readOnly && (keyName === 'command+Backspace' ||
                                keyName === 'ctrl+Backspace')
                                // keyName === 'delete'
                            ) {
                                e.preventDefault();
                                // closeMessage();
                                // setShowFoldersSearch(true);
                                if (!showDeletedMessages) {
                                    trashAll();
                                } else {
                                    setShowDeleteModal(true);
                                }
                                customAnalytics.ui_action({
                                    "action": "Message Delete",
                                    "shortcut_key": keyName,
                                    "screen": "MessageScreen"
                                })
                            }
                            // if (keyName === 'command+P') {
                            //     e.preventDefault();
                            // }
                            if (keyName === 'command+.' || keyName === 'ctrl+.') {
                                e.preventDefault();
                                closeMessage();
                                customAnalytics.ui_action({
                                    "action": "Message Close",
                                    "shortcut_key": keyName,
                                    "screen": "MessageScreen"
                                })
                            }
                            if (
                                !disableMoveMessage &&
                                (
                                    keyName === 'command+J' ||
                                    keyName === 'ctrl+J' ||
                                    keyName === 'command+j' ||
                                    keyName === 'ctrl+j'
                                )
                            ) {
                                e.preventDefault();
                                openMoveMessageModal(newMessage);
                                customAnalytics.ui_action({
                                    "action": "Message Move",
                                    "shortcut_key": keyName,
                                    "screen": "MessageScreen"
                                })
                            }
                        }}
                        filter={event => {
                            return true;
                        }}
                    >
                        <div
                            style={{
                                height: '100%',
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative'
                            }}
                        >
                            {isNoteMode
                                ? (
                                    <Formik
                                        innerRef={formikRef}
                                        initialValues={{
                                            title: newMessage.title || '',
                                            body: isXBookmark ? localBody : (newMessage.body || '')
                                        }}
                                        enableReinitialize
                                        onSubmit={async values => {
                                            if (readOnly) return;
                                            const prevTitle = newMessage.title || '';
                                            const prevBody = isXBookmark
                                                ? normalizeMarkdownForStorage(localBody || '')
                                                : normalizeMarkdownForStorage(newMessage.body || '');
                                            const nextTitle = (values.title || '');
                                            const nextBody = normalizeMarkdownForStorage(values.body || '');

                                            if (isXBookmark) {
                                                if (prevBody !== nextBody) {
                                                    handleLocalBodySave(nextBody);
                                                }
                                                return;
                                            }

                                            if (prevTitle === nextTitle && prevBody === nextBody) {
                                                return;
                                            }
                                            const optimisticMessage = {
                                                ...newMessage,
                                                title: nextTitle,
                                                body: nextBody,
                                                updatedAt: new Date().toISOString()
                                            };
                                            applyLocalMessageUpdate(optimisticMessage);

                                            const updatedMessage = await updateMessage(message, {
                                                ...values,
                                                body: nextBody
                                            });
                                            if (updatedMessage) {
                                                if (!isClosingRef.current) {
                                                    selectPreviewMessage(applyPreviewSelectionState(updatedMessage));
                                                }
                                            }
                                        }}
                                        validationSchema={Yup.object().shape({
                                            title: Yup.string(),
                                            body: Yup.string()
                                        })}
                                    >
                                        {({ values, setFieldValue }) => (
                                            <React.Fragment>
                                                {renderNoteHeader(newMessage, values)}
                                                <div
                                                    style={{
                                                        overflowY: 'auto',
                                                        paddingBottom: 76,
                                                        paddingLeft: 12,
                                                        paddingRight: 12,
                                                        flex: 1,
                                                        minHeight: 0
                                                    }}
                                                >
                                                    <MessageContainer>
                                                        <NoteSection
                                                            readOnly={readOnly}
                                                            isNoteMode
                                                            isEditing={isNoteEditing}
                                                            values={values}
                                                            setFieldValue={setFieldValue}
                                                            onBackToMessage={handleStopNoteEditing}
                                                            onOpenNote={() => handleNoteClick(newMessage)}
                                                            onStartEditing={handleStartNoteEditing}
                                                            noteEditorMode={noteEditorMode}
                                                            onNoteEditorModeChange={setNoteEditorMode}
                                                            onCopyNote={noteText => {
                                                                copyTextToClipboard((noteText || '').trim());
                                                            }}
                                                            onTaskCheckboxToggle={({ taskIndex, checked }) =>
                                                                handlePreviewTaskCheckboxToggle(
                                                                    newMessage,
                                                                    values,
                                                                    setFieldValue,
                                                                    taskIndex,
                                                                    checked
                                                                )
                                                            }
                                                        />
                                                        {renderMessageInfo(newMessage)}
                                                    </MessageContainer>
                                                </div>
                                            </React.Fragment>
                                        )}
                                    </Formik>
                                )
                                : renderHeader(newMessage)}
                            
                            {!isNoteMode ? (
                                <PreviewScroll>
                                <MessageContainer ref={messageContainerRef}>
                                    <MessageReferencePreview
                                        message={newMessage}
                                        onOpenMessage={selectPreviewMessage}
                                    />
                                    <React.Fragment>
                                            {/* <Button onClick={closeMessage}>Close</Button> */}

                                            <Formik
                                                innerRef={formikRef}
                                                initialValues={{
                                                    title: newMessage.title || '',
                                                    body: isXBookmark ? localBody : (newMessage.body || '')
                                                }}
                                                enableReinitialize
                                                onSubmit={async (values, actions) => {
                                                    if (readOnly) return;
                                                    const prevTitle = newMessage.title || '';
                                                    const prevBody = isXBookmark
                                                        ? normalizeMarkdownForStorage(localBody || '')
                                                        : normalizeMarkdownForStorage(newMessage.body || '');
                                                    const nextTitle = (values.title || '');
                                                    const nextBody = normalizeMarkdownForStorage(values.body || '');

                                                    if (isXBookmark) {
                                                        if (prevBody !== nextBody) {
                                                            handleLocalBodySave(nextBody);
                                                        }
                                                        return;
                                                    }

                                                    if (prevTitle === nextTitle && prevBody === nextBody) {
                                                        return;
                                                    }
                                                    const optimisticMessage = {
                                                        ...newMessage,
                                                        title: nextTitle,
                                                        body: nextBody,
                                                        updatedAt: new Date().toISOString()
                                                    };
                                                    applyLocalMessageUpdate(optimisticMessage);

                                                    const updatedMessage = await updateMessage(message, {
                                                        ...values,
                                                        body: nextBody
                                                    });
                                                    if (updatedMessage) {
                                                        // Only update selectedMessage if we're not closing
                                                        // This prevents the preview from reopening after close
                                                        if (!isClosingRef.current) {
                                                            // Hook already broadcasts the update event, just update local state
                                                            selectPreviewMessage(applyPreviewSelectionState(updatedMessage));
                                                        }
                                                    }
                                                }}
                                                validationSchema={Yup.object().shape({
                                                    title: Yup.string(),
                                                    body: Yup.string()
                                                })}
                                            >
                                                {({ values, handleSubmit, handleChange, handleBlur, setFieldValue, errors, touched }) => (
                                                    <div>
                                                        <form onSubmit={handleSubmit}>
                                                            <>
                                                                {!isXBookmark && (
                                                                    <>
                                                                        <div
                                                                            style={{
                                                                                display: 'flex',
                                                                                flexDirection: 'row',
                                                                                marginBottom: 8
                                                                            }}
                                                                        >
                                                                            {!readOnly ? <Checkbox message={newMessage}></Checkbox> : null}
                                                                            <div
                                                                                style={{
                                                                                    flex: 1,
                                                                                    minWidth: 0,
                                                                                    padding: 0,
                                                                                    backgroundColor: 'transparent'
                                                                                }}
                                                                            >
                                                                                {isTitleEditing && !readOnly ? (
                                                                                    <TextareaWrapper>
                                                                                        <TextareaAutosize
                                                                                            ref={titleInputRef}
                                                                                            onKeyDown={event => {
                                                                                                if (event.keyCode == 13 && event.shiftKey) {
                                                                                                    event.stopPropagation();
                                                                                                } else if (event.keyCode == 13) {
                                                                                                    event.preventDefault();
                                                                                                    setIsTitleEditing(false);
                                                                                                    handleSubmit();
                                                                                                } else if (event.key === 'Escape') {
                                                                                                    event.preventDefault();
                                                                                                    event.stopPropagation();
                                                                                                    setIsTitleEditing(false);
                                                                                                    event.currentTarget.blur();
                                                                                                }
                                                                                            }}
                                                                                            style={styles.titleInputStyle}
                                                                                            maxRows={24}
                                                                                            type="text"
                                                                                            name="title"
                                                                                            onChange={handleChange}
                                                                                            onBlur={event => {
                                                                                                handleBlur(event);
                                                                                                setIsTitleEditing(false);
                                                                                                handleSubmit();
                                                                                            }}
                                                                                            value={values.title}
                                                                                            placeholder={lnT('messages:title_7e8cd205')}
                                                                                        ></TextareaAutosize>
                                                                                    </TextareaWrapper>
                                                                                ) : (
                                                                                    <TitleCard
                                                                                        role={readOnly ? undefined : 'button'}
                                                                                        tabIndex={readOnly ? undefined : 0}
                                                                                        onKeyDown={event => {
                                                                                            if (event.target !== event.currentTarget) return;
                                                                                            if (!readOnly && (event.key === 'Enter' || event.key === ' ')) {
                                                                                                event.preventDefault();
                                                                                                setIsTitleEditing(true);
                                                                                            }
                                                                                        }}
                                                                                        onClick={event => {
                                                                                            if (event.target instanceof Element && event.target.closest('a, button, input, [data-message-interactive], [role="dialog"]:not([data-video-workspace-modal])')) return;
                                                                                            event.preventDefault();
                                                                                            event.stopPropagation();
                                                                                            if (!readOnly) setIsTitleEditing(true);
                                                                                        }}
                                                                                    >
                                                                                        <TitleTextDisplay $readOnly={readOnly} $isEmpty={!values.title}>
                                                                                            <TitleContent
                                                                                                title={values.title}
                                                                                                messageId={newMessage.id}
                                                                                                onEdit={() => setIsTitleEditing(true)}
                                                                                                placeholder={!readOnly ? lnT('messages:addTitle_698fcda4') : ''}
                                                                                                readOnly={readOnly}
                                                                                                onTaskCheckboxToggle={({ taskIndex, checked }) =>
                                                                                                    handlePreviewTitleTaskCheckboxToggle(newMessage, values, setFieldValue, taskIndex, checked)
                                                                                                }
                                                                                            />
                                                                                        </TitleTextDisplay>
                                                                                        <CardHoverActions>
                                                                                            <CardHoverActionButton
                                                                                                type="button"
                                                                                                disabled={!values.title}
                                                                                                onMouseDown={event => {
                                                                                                    event.preventDefault();
                                                                                                }}
                                                                                                onClick={event => {
                                                                                                    event.preventDefault();
                                                                                                    event.stopPropagation();
                                                                                                    copyTextToClipboard((values.title || '').trim());
                                                                                                }}
                                                                                            >
                                                                                                <FiCopy size={12} />
                                                                                            </CardHoverActionButton>
                                                                                            {!readOnly ? (
                                                                                            <CardHoverActionButton
                                                                                                type="button"
                                                                                                onMouseDown={event => {
                                                                                                    event.preventDefault();
                                                                                                }}
                                                                                                onClick={event => {
                                                                                                    event.preventDefault();
                                                                                                    event.stopPropagation();
                                                                                                    if (!readOnly) setIsTitleEditing(true);
                                                                                                }}
                                                                                            >
                                                                                                <FiEdit2 size={12} />
                                                                                            </CardHoverActionButton>) : null}
                                                                                        </CardHoverActions>
                                                                                    </TitleCard>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </>
                                                                )}
                                                                {isXBookmark && (
                                                                    <div style={{ display: 'flex', marginBottom: 8 }}>
                                                                        {!readOnly ? <Checkbox message={newMessage}></Checkbox> : null}
                                                                    </div>
                                                                )}
                                                            </>

                                                            <NoteSection
                                                        readOnly={readOnly}
                                                                inline
                                                                messageId={newMessage.id}
                                                                isNoteMode={false}
                                                                isEditing={isNoteEditing}
                                                                values={values}
                                                                setFieldValue={setFieldValue}
                                                                onBackToMessage={handleStopNoteEditing}
                                                                onOpenNote={() => handleNoteClick(newMessage)}
                                                                onStartEditing={handleStartNoteEditing}
                                                                noteEditorMode={noteEditorMode}
                                                                onNoteEditorModeChange={setNoteEditorMode}
                                                                onCopyNote={noteText => {
                                                                    copyTextToClipboard((noteText || '').trim());
                                                                }}
                                                                onTaskCheckboxToggle={({ taskIndex, checked }) =>
                                                                    handlePreviewTaskCheckboxToggle(
                                                                        newMessage,
                                                                        values,
                                                                        setFieldValue,
                                                                        taskIndex,
                                                                        checked
                                                                    )
                                                                }
                                                            />
                                                            {renderTopDownMetadata(newMessage)}
                                                            {renderTopDownActions(newMessage, values)}
                                                        </form>
                                                    </div>
                                                )}
                                            </Formik>
                                            <PluginManager
                                                readOnly={readOnly}
                                                canGenerate={canGenerate}
                                                message={newMessage}
                                                files={dropzoneFiles}
                                                clearDropzoneFiles={() => setDropzoneFiles(undefined)}
                                                showAttachAction={false}
                                                onOpenVideoWorkspace={onOpenVideoWorkspace}
                                                hideUrlTranscriptAction={hideUrlTranscriptAction}
                                                onOpenArticleVoiceModal={(articleMessage, urlMetaIndex, articleText) => {
                                                    setVoiceModalUrlMetaIndex(urlMetaIndex);
                                                    openVoiceModal(articleMessage, 'ARTICLE', articleText || '');
                                                }}
                                                isGeneratingArticleAudio={isGeneratingArticleAudio}
                                            />
                                            <EntityMentionsSection
                                                entities={newMessage?.entities}
                                                messageId={newMessage?.id}
                                                canRemove={!readOnly && !newMessage?.deletedAt}
                                                onSearch={searchEntityMention}
                                            />
                                            <EventSection message={newMessage} readOnly={readOnly} />
                                            <ExtractedArtifactsSection artifacts={(newMessage?.artifacts || []).filter(artifact => String(artifact.id) !== String(newMessage?.eventArtifactId || ''))} />
                                            {renderMessageInfo(newMessage)}
                                    </React.Fragment>
                                </MessageContainer>
                                </PreviewScroll>
                            ) : null}
                            {!isNoteMode && !readOnly ? (
                                <BottomActions>
                                    <BottomActionIconButton
                                        type="button"
                                        onClick={() => attachmentInputRef.current?.click()}
                                        disabled={showDeletedMessages || Boolean(newMessage.deletedAt)}
                                        title={lnT('messages:attachFile')}
                                        aria-label={lnT('messages:attachFile')}
                                    >
                                        <FiPaperclip size={22} />
                                    </BottomActionIconButton>
                                    <input
                                        ref={attachmentInputRef}
                                        type="file"
                                        multiple
                                        hidden
                                        onChange={handleAttachmentSelection}
                                    />
                                </BottomActions>
                            ) : null}
                        </div>
                    </Hotkeys>
                </MyDropzone>
                {sharingMessage && (
                    <React.Suspense fallback={null}>
                        <MessageShareModal key={sharingMessage.id} isOpen message={sharingMessage}
                            closeModal={() => setSharingMessage(null)} />
                    </React.Suspense>
                )}
                <DeleteModal
                    isOpen={showDeleteModal}
                    title={lnT('messages:thisMessageWillBeDeletedForever')}
                    description={lnT('folders:thisMessageWillBeDeletedAndYou')}
                    closeModal={async response => {
                        if (response) {
                            deletePermanently();
                        }
                        setShowDeleteModal(false);
                    }}
                ></DeleteModal>
                <DeleteModal
                    isOpen={showReminderTrashWarning}
                    title={lnT('messages:thisMessageHasAReminder')}
                    description={lnT('messages:trashingThisMessageWillCancelTheReminder', { value1: String(formatReminderDate(message.reminderAt)) })}
                    closeButtonText={lnT('messages:keepIt')}
                    actionButtonText={lnT('folders:trashAnyway')}
                    closeModal={(confirmed) => {
                        setShowReminderTrashWarning(false);
                        if (confirmed) {
                            executeTrash();
                        }
                    }}
                />
                <ReminderPickerModal
                    isOpen={Boolean(reminderPickerMessage)}
                    initialReminderAt={reminderPickerMessage?.reminderAt || reminderPickerMessage?.reminderFiredAt}
                    initialReminderFiredAt={reminderPickerMessage?.reminderAt ? null : reminderPickerMessage?.reminderFiredAt}
                    initialDeliveryType={reminderPickerMessage?.reminderDeliveryType}
                    initialReminderMessage={reminderPickerMessage?.reminderMessage}
                    initialAlerts={reminderPickerMessage?.alerts}
                    loading={setMessageReminderLoading}
                    onClose={() => setReminderPickerMessage(null)}
                    onSubmit={submitReminder}
                    onClearReminder={reminderPickerMessage?.reminderAt ? async () => {
                        const msg = reminderPickerMessage;
                        setReminderPickerMessage(null);
                        await clearReminder(msg);
                    } : undefined}
                />
                <SearchFoldersModal
                    isOpen={showMoveFoldersModal && !disableMoveMessage}
                    action={searchActions.MOVE_MESSAGES}
                    title={lnT('folders:moveMessageTo')}
                    addNewMoveMessage
                    dropdownMessage={moveMessageTarget}
                    closeModal={toFolder => {
                        if (toFolder && moveMessageTarget) {
                            // Keep the moved folder visible in this preview without re-selecting the
                            // message globally. Re-selection can switch a chat-backed preview into
                            // its chat composer and steal focus after the modal closes.
                            if (String(newMessage?.id || '') === String(moveMessageTarget?.id || '')) {
                                setLocalFolderOverride({
                                    messageId: String(newMessage.id),
                                    folder: toFolder
                                });
                            }
                            emitMessageUpdate({
                                ...moveMessageTarget,
                                folder: toFolder,
                                updatedAt: new Date().toISOString()
                            });
                        }
                        setShowMoveFoldersModal(false);
                        setMoveMessageTarget(undefined);
                    }}
                />
                <MessageVersionHistoryModal
                    isOpen={showVersionHistoryModal}
                    message={newMessage}
                    onClose={() => setShowVersionHistoryModal(false)}
                    onRestored={restoredMessage => {
                        applyLocalMessageUpdate(restoredMessage);
                    }}
                    showDefaultNotification={showDefaultNotification}
                />
                <Modal
                    isOpen={showVoiceModal}
                    onRequestClose={closeVoiceModal}
                    style={voiceModalStyles}
                    contentLabel={lnT('messages:voiceSettings')}
                >
                    <AudioCreateContent
                        sources={voiceModalSource === 'ARTICLE' ? [] : [
                            { id: 'TITLE', label: lnT('messages:title_7e8cd205'), text: voiceModalSourceMessage?.title || '' },
                            { id: 'NOTE', label: lnT('messages:note'), text: voiceModalSourceMessage?.body || '' }
                        ]}
                        source={voiceModalSource}
                        onSourceChange={source => {
                            setVoiceModalSource(source);
                            setVoiceModalText(source === 'TITLE' ? voiceModalSourceMessage?.title || '' : voiceModalSourceMessage?.body || '');
                        }}
                        excerpt={voiceModalExcerpt}
                        voices={serverVoices.voices}
                        voicesLoading={serverVoices.loading} voicesError={serverVoices.error}
                        voice={selectedTtsVoice}
                        onVoiceChange={nextVoice => {
                            cleanupVoicePreview();
                            setSelectedTtsVoice(nextVoice);
                            window.localStorage.setItem(TTS_VOICE_STORAGE_KEY, nextVoice);
                            playVoiceSample(nextVoice);
                        }}
                        onPreviewToggle={handlePreviewVoiceToggle}
                        playing={isVoicePreviewPlaying}
                        previewing={isPreviewingVoice}
                        generating={isGeneratingAudio}
                        canContinue={canGenerateFromVoiceModal}
                        onContinue={handleGenerateFromVoiceModal}
                        onClose={closeVoiceModal}
                    >
                            <audio
                                ref={voicePreviewAudioRef}
                                preload="none"
                                onPlay={() => setIsVoicePreviewPlaying(true)}
                                onPause={() => setIsVoicePreviewPlaying(false)}
                                onEnded={() => setIsVoicePreviewPlaying(false)}
                                style={{ display: 'none' }}
                            />
                    </AudioCreateContent>
                </Modal>
                <CreditsRequiredModal
                    isOpen={showCreditsRequiredModal}
                    description={creditsRequiredDescription}
                    onRequestClose={() => {
                        setShowCreditsRequiredModal(false);
                        setCreditsRequiredDescription('This action uses Lucky Credits. Add Lucky Credits to continue.');
                    }}
                    onCheckout={() => {
                        setShowCreditsRequiredModal(false);
                        setCreditsRequiredDescription('This action uses Lucky Credits. Add Lucky Credits to continue.');
                        openSettingsModal(SETTINGS_MODAL_TAB.UPGRADES);
                    }}
                />
            </React.Fragment>
        );
    }
});

const styles = {
    titleInputStyle: {
        width: '100%',
        outline: 0,
        border: 'none',
        fontSize: 17,
        fontFamily: 'inherit',
        fontWeight: 700,
        letterSpacing: '-0.2px',
        lineHeight: 1.35,
        padding: '4px 0',
        backgroundColor: 'transparent',
        color: 'rgba(255,255,255,0.95)',
        resize: 'none'
    },
    bodyInputStyle: {
        width: '100%',
        outline: 0,
        padding: 0,
        border: 'none',
        fontSize: 14,
        resize: 'none',
        backgroundColor: NOTE_BACKGROUND
    }
};



const FolderTitle = styled.div`
    cursor: pointer;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 4px;
    margin-top: ${props => props.$isActive ? '4px' : 0};
    font-size: ${props => props.$isActive ? '12px' : '17px'};
    line-height: ${props => props.$isActive ? '16px' : '21px'};
    font-weight: ${props => props.$isActive ? 400 : 600};
    color: ${props => props.$isActive ? PALE_WHITE_6 : WHITE};

    span {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &:hover {
        color: ${props => props.$isActive ? WHITE : PALE_WHITE_6};
    }
`;

const ReminderMetadataCard = styled.button`
    width: 100%;
    min-height: 52px;
    margin: 0 0 8px;
    padding: 8px 14px;
    border-radius: 14px;
    border: 1px solid ${props => props.$isFiredReminder ? GREEN : 'rgba(92, 170, 255, 0.72)'};
    background: transparent;
    color: ${WHITE};
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    cursor: pointer;
`;

const ReminderMetadataLabel = styled.span`
    min-width: 0;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 14px;
`;

const ReminderMetadataValue = styled.span`
    min-width: 0;
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    color: ${props => props.$isFiredReminder ? GREEN : 'rgba(134, 193, 255, 0.96)'};
    font-size: 13px;
    text-align: right;
`;

const TopDownActions = styled.div`
    display: flex;
    gap: 10px;
    width: 100%;
    margin-bottom: 10px;
    padding-bottom: 2px;
    overflow-x: auto;
    overscroll-behavior-x: contain;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const TopDownActionButton = styled.button`
    flex: 0 0 auto;
    height: 38px;
    padding: 0 12px;
    border-radius: 12px;
    border: 1px solid ${PALE_WHITE_1};
    background: rgba(255, 255, 255, 0.03);
    color: rgba(255, 255, 255, 0.86);
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;

    &:hover {
        background: rgba(255, 255, 255, 0.08);
        color: ${WHITE};
    }
`;

const BottomActions = styled.div`
    position: absolute;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 20;
    min-height: 52px;
    padding: 8px 4px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    background: ${PRIMARY_COLOR_8};
    backdrop-filter: saturate(180%) blur(18px);
    -webkit-backdrop-filter: saturate(180%) blur(18px);
    border-top: 1px solid rgba(255, 255, 255, 0.04);
`;

const BottomActionIconButton = styled.button`
    min-width: 36px;
    min-height: 36px;
    padding: 4px 6px;
    border: none;
    background: transparent;
    color: ${PALE_WHITE_6};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.35 : 1};

`;

const ActionButton = styled.div`
    cursor: pointer;
    height: 100%;
    display: flex;
    align-items: center;
    border-radius: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 6px;
    padding-right: 6px;
    color: ${PALE_WHITE_6};
    &:hover {
        color: ${WHITE};
        background-color: ${PALE_WHITE_1};
    }
`;

const TextareaWrapper = styled.div`
    width: 100%;
    // padding: 0.5rem;
    // margin-left: 10px;
    // margin-right: 10px;
    // border-radius: 4px;
    // padding: 0.5rem;
    // background-color: white;
`;

const CardHoverActions = styled.div`
    position: absolute;
    top: 4px;
    right: 0;
    z-index: 1;
    background: #222729;
    box-shadow: -6px 0 10px #222729;
    border-radius: 6px;
    display: flex;
    gap: 4px;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s ease;
`;

const CardHoverActionButton = styled.button`
    border: none;
    outline: none;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.08);
    color: rgba(255, 255, 255, 0.8);
    cursor: ${props => props.disabled ? 'default' : 'pointer'};
    opacity: ${props => props.disabled ? 0.35 : 1};
    pointer-events: ${props => props.disabled ? 'none' : 'auto'};
    &:hover {
        background: rgba(255, 255, 255, 0.14);
        color: ${WHITE};
    }
`;

const TitleCard = styled.div`
    position: relative;
    cursor: pointer;
    &:hover ${CardHoverActions}, &:focus-within ${CardHoverActions} {
        opacity: 1;
        pointer-events: auto;
    }
`;


const LeftSection = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
`;

const NoteOverlayHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    color: ${PALE_WHITE_6};
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 12px;
    padding-right: 12px;
    flex-shrink: 0;
    background-color: transparent;
`;

const ActionMenuContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
`;

const ActionMenu = styled.div`
    min-width: 220px;
    max-width: 300px;
    background: rgba(20, 24, 28, 0.96);
    border: 1px solid ${PALE_WHITE_1};
    color: ${WHITE};
    z-index: ${props => props.$zIndex};
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.28);
    border-radius: 14px;
    overflow: hidden;
    backdrop-filter: saturate(180%) blur(24px);
    -webkit-backdrop-filter: saturate(180%) blur(24px);
`;

const ActionMenuShortcut = styled.span`
    margin-left: auto;
    min-width: 56px;
    text-align: right;
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.54);
    opacity: 0;
    transition: opacity 0.15s ease;
`;

const ActionMenuItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    cursor: pointer;
    user-select: none;
    &:hover {
        background-color: rgba(255, 255, 255, 0.08);
    }
    &:hover ${ActionMenuShortcut} {
        opacity: 1;
    }
`;

const ActionMenuSeparator = styled.div`
    height: 1px;
    background-color: rgba(255, 255, 255, 0.1);
    margin: 2px 0;
`;

const ActionMenuMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 12px;
    font-size: 12px;
    color: rgba(255, 255, 255, 0.82);
`;

const ActionMenuIcon = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: ${props => (props.destructive ? '#ff7a7a' : 'rgba(255, 255, 255, 0.78)')};
`;

const DestructiveActionLabel = styled.span`
    color: #ff7a7a;
`;

const InlineRawToggleButton = styled.button`
    border: none;
    background: transparent;
    color: ${WHITE};
    height: 32px;
    padding: 0 2px 0 4px;
    border-radius: 0;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: opacity 0.15s ease;

    &:hover {
        opacity: 0.85;
    }
`;

const InlineRawToggleLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
`;

const HeaderIconButton = styled.button`
    border: none;
    background: transparent;
    color: ${PALE_WHITE_6};
    height: 100%;
    display: inline-flex;
    align-items: center;
    border-radius: 8px;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 6px;
    padding-right: 6px;
    cursor: ${props => (props.disabled ? 'default' : 'pointer')};
    opacity: ${props => (props.disabled ? 0.35 : 1)};

    &:hover {
        color: ${props => (props.disabled ? PALE_WHITE_6 : WHITE)};
        background-color: ${props => (props.disabled ? 'transparent' : PALE_WHITE_1)};
    }
`;



const ActionMenuToggle = styled.span`
    margin-left: auto;
    width: 30px;
    height: 18px;
    border-radius: 999px;
    background: ${props => (props.$isActive ? '#1f2426' : 'rgba(0, 0, 0, 0.18)')};
    border: 1px solid ${props => (props.$isActive ? '#1f2426' : 'rgba(0, 0, 0, 0.12)')};
    display: inline-flex;
    align-items: center;
    padding: 1px;
    transition: background 0.15s ease, border-color 0.15s ease;
`;

const ActionMenuToggleKnob = styled.span`
    width: 14px;
    height: 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.98);
    transform: ${props => (props.$isActive ? 'translateX(12px)' : 'translateX(0)')};
    transition: transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
`;

export default MessagePreviewComponent;
